/* Optimal — iOS design layer JS.
   1. Layout hooks: header height + stable viewport height as CSS vars.
   2. Sheet fade-in reveal (IntersectionObserver).
   3. Mobile homepage pager: takes over vertical swipes and animates one
      sheet per gesture on the GPU (finger-follow drag, spring release).
      Native scroll stays untouched on desktop, other pages, reduced motion,
      inside drawers/modals, and inside sheets that scroll internally. */
(function () {
  var setup = function () {
    var isIndex = document.body && document.body.dataset.page === 'index';
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var docEl = document.documentElement;

    /* ---------- Layout hooks ---------- */
    if (isIndex) {
      docEl.classList.add('ol-index');

      var lastHeaderH = null;
      var setVars = function () {
        var header = document.querySelector('.section-header');
        if (header) {
          // Sheet heights were tuned with this var at 0px (the overlay header
          // used to measure collapsed before pager mode applied pre-paint).
          // Keep 0 in pager mode so sheet 2 doesn't shrink and expose the
          // next sheet's content.
          var h = docEl.classList.contains('ol-pager') ? 0 : header.offsetHeight;
          if (h !== lastHeaderH) {
            lastHeaderH = h;
            docEl.style.setProperty('--ol-header-h', h + 'px');
          }
        }
      };
      setVars();
      window.addEventListener('resize', setVars);
    }

    /* ---------- Sheet fade-in reveal ---------- */
    if (isIndex && !reduced && 'IntersectionObserver' in window) {
      docEl.classList.add('ios-anim');

      var sheets = document.querySelectorAll('#MainContent > .shopify-section');
      if (isMobile) {
        // Pager mode: the swipe itself is the entrance, so every sheet is
        // revealed up front — content is fully painted before it slides in,
        // nothing fades in after arrival. (Double rAF keeps the initial
        // page-load fade for what's on screen.)
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            sheets.forEach(function (sheet) {
              sheet.classList.add('ios-sheet-in');
            });
          });
        });
      } else {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                entry.target.classList.add('ios-sheet-in');
                observer.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
        );
        sheets.forEach(function (sheet) {
          if (sheet.getBoundingClientRect().top < window.innerHeight * 0.9) {
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                sheet.classList.add('ios-sheet-in');
              });
            });
          } else {
            observer.observe(sheet);
          }
        });
      }
    }

    /* ---------- Sheet video: plays while its sheet is on screen ----------
       The pager moves sheets with a transform, which IntersectionObserver
       accounts for, so a plain viewport observer is enough — no need to hook
       the pager's own index.

       Sound: the clip is silent by design. It stays muted for its whole
       life — which is also what keeps autoplay legal on mobile — and there
       is no control offered to turn it on.
    */
    var sheetVideos = document.querySelectorAll('[data-ol-sheet-video] video');
    if (sheetVideos.length && 'IntersectionObserver' in window) {
      Array.prototype.forEach.call(sheetVideos, function (video) {
        var shell = video.closest('[data-ol-sheet-video]');
        var overlay = shell && shell.querySelector('[data-ol-video-overlay]');
        var nextCue = shell && shell.querySelector('[data-ol-video-next]');
        var nextBtn = shell && shell.querySelector('[data-ol-video-next-btn]');
        var lineEl = shell && shell.querySelector('[data-ol-video-line]');
        var progressEl = shell && shell.querySelector('[data-ol-video-progress]');

        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');

        var wanted = false; // is this sheet the one on screen?

        var play = function () {
          if (!wanted || document.hidden || stopped) return;
          var p = video.play();
          if (p && p.catch) p.catch(function () {});
        };

        // Retry once the data actually arrives — on a cold cellular load the
        // first play() lands while readyState is still 0.
        ['loadeddata', 'canplay', 'canplaythrough'].forEach(function (ev) {
          video.addEventListener(ev, play);
        });

        /* Muted autoplay is legal, but it is not guaranteed: Safari refuses
           it outright in Low Power Mode, and honours a per-site "never
           auto-play" setting. The element has no poster, so a refusal shows
           a black card and nothing ever asks again.

           So every gesture anywhere on the page is a chance to start it. The
           first one also unlocks the element for later — play() called
           inside a real gesture clears the restriction, and if this sheet is
           not the one on screen the clip is put straight back to the top.
           Swiping through the stack provides the gesture long before this
           sheet arrives. */
        var unlocked = false;
        var gestureKick = function () {
          if (!video.paused) return; // already running
          if (wanted) {
            play(); // on screen and stalled — this gesture is the retry
            return;
          }
          if (unlocked) return;
          unlocked = true;
          var p = video.play();
          var settle = function () {
            if (wanted) return;
            video.pause();
            try {
              if (video.currentTime > 0.05) video.currentTime = 0;
            } catch (e) {}
          };
          if (p && p.then) p.then(settle, settle);
          else settle();
        };
        ['pointerdown', 'touchstart', 'keydown'].forEach(function (ev) {
          document.addEventListener(ev, gestureKick, { passive: true });
        });

        /* The clip is stopped here rather than at its own duration. I
           sampled the closing animation frame by frame: the Optimal logo
           slides in at ~26.2s, sits centred and crisp from 26.5s, and starts
           to blur out at ~27.5s. 27.15s is the middle of that hold, so the
           sheet settles on the logo dead centre. */
        var END = 27.15;

        /* Caption cues, in seconds of the CLIP's own timeline — not of the
           sheet being on screen. Driving them off currentTime is what keeps
           the copy in step when playback starts late or stalls to buffer.

           The footage runs: daylight to 13.8s, a cut to black 14.0-15.7s,
           then the night reveal. The empty cue clears the caption across the
           cut. Each line holds 5-6 seconds so it can actually be read.

           Copy is product-led, and every claim is lifted from the kit's own
           description (full interior coverage across dash/doors/footwells,
           animation modes plus solid colors) — keep it that way if you
           retime or rewrite these. */
        var CUES = [
          { at: 2.2, line: 'YOUR INTERIOR, REIMAGINED.' },
          { at: 8.5, line: 'DASH, DOORS, FOOTWELLS.' },
          { at: 13.5, line: '' },
          /* Just after the beat. I pulled the audio out as a 20ms RMS
             envelope: the track sits near silence (0.011) at 15.84s and
             slams to 0.716 at 15.88s. This sits a third of a second past
             that transient, still inside the loud section. `hit` swaps the
             slow settle for a fast punch so the line arrives with the music
             rather than easing in over it. */
          { at: 16.2, line: 'ANY COLOR, ANY MOOD.', hit: true },
          { at: 23.8, line: 'MAKE IT YOURS.', cta: true },
        ];

        var cueIndex = -1;
        var ctaShown = false;
        var stopped = false;
        var rafId = null;

        var showLine = function (text, hit) {
          if (!lineEl) return;
          var swap = function () {
            lineEl.textContent = text;
            lineEl.classList.remove('is-out', 'is-in', 'is-hit');
            if (!text) return;
            // The reflow is load-bearing: without it the class removal and
            // re-add are coalesced into one style pass and nothing replays.
            void lineEl.offsetWidth;
            lineEl.classList.add(hit ? 'is-hit' : 'is-in');
          };
          // Only fade the old line out if there was one.
          // A beat-drop line never waits on a fade-out — it has to be there
          // on the transient.
          if (lineEl.textContent && !hit) {
            lineEl.classList.remove('is-in');
            void lineEl.offsetWidth;
            lineEl.classList.add('is-out');
            setTimeout(swap, 460);
          } else {
            swap();
          }
        };

        var applyCue = function (i) {
          if (i === cueIndex) return;
          cueIndex = i;
          if (i < 0) {
            showLine('');
            return;
          }
          showLine(CUES[i].line, CUES[i].hit);
          if (CUES[i].cta && !ctaShown && nextCue) {
            ctaShown = true;
            nextCue.classList.add('is-in');
          }
        };

        var tick = function () {
          if (!wanted) return;
          var t = video.currentTime;

          if (t >= END) {
            // Freeze on the centred logo and stay there. timeupdate only
            // fires ~4x a second, so the clamp is what guarantees the exact
            // frame rather than wherever playback happened to land.
            stopped = true;
            video.pause();
            if (Math.abs(video.currentTime - END) > 0.03) video.currentTime = END;
            t = END;
          }

          var next = -1;
          for (var i = 0; i < CUES.length; i++) {
            if (t >= CUES[i].at) next = i;
          }
          applyCue(next);
          if (progressEl) {
            progressEl.style.width = Math.min(100, (t / END) * 100).toFixed(2) + '%';
          }
        };

        // rAF rather than timeupdate alone: 60Hz instead of ~4Hz, which is
        // what lets the clip land on the logo frame and keeps the scrubber
        // smooth. timeupdate stays as a backstop for backgrounded tabs.
        var pump = function () {
          tick();
          rafId = wanted && !stopped ? requestAnimationFrame(pump) : null;
        };
        video.addEventListener('timeupdate', tick);

        // Leaving the sheet and coming back replays the whole thing from the
        // top — including the arrow, which is re-armed here so it lands on
        // its cue again rather than being on screen from the first frame.
        var restart = function () {
          stopped = false;
          cueIndex = -1;
          ctaShown = false;
          if (nextCue) nextCue.classList.remove('is-in');
          showLine('');
          try {
            // Seeking a clip that is already at the top only costs a decode
            // — and on Safari that decode is a black frame.
            if (video.currentTime > 0.05) video.currentTime = 0;
          } catch (e) {}
        };

        /* The rewind is done while the sheet is OFF SCREEN, not on the way
           back in. Seeking costs a decode, and paying for it on arrival is
           what makes the clip hitch — or show a black frame on Safari —
           just as the sheet lands. Waiting out the snap animation first
           keeps the outgoing frame intact for the whole transition (the
           sheet is still partly on screen while it plays), and by the time
           anyone swipes back the element is already sitting decoded on
           frame 0, so play() picks up instantly from the top. */
        /* Hysteresis, not a single threshold. The sheet has to be half on
           screen to START, but has to be all but gone to STOP. A single 0.5
           line meant a half-swipe — dragging partway and letting go — pushed
           the sheet under it, wiped the caption and paused the clip, then
           replayed from the top on the snap back. With the two levels apart,
           an incomplete swipe changes nothing at all. */
        var ENTER_RATIO = 0.5;
        /* 0.15, not something tiny: once the pager has settled on the NEXT
           sheet this one still peeks ~44px at the top of the screen, which
           is a ratio of about 0.05 — anything below that and the clip keeps
           playing (and sounding) off screen forever. 0.15 clears the peek
           while still leaving a wide dead band for an incomplete swipe. */
        var LEAVE_RATIO = 0.15;

        var REWIND_DELAY = 420; // just past the shortest snap animation
        var rewindTimer = null;
        var lastRatio = 0;

        /* Only rewind once the sheet is genuinely out of sight. `wanted`
           going false only means this is no longer the current sheet — on a
           slow drag back the sheet can still be a third on screen when the
           timer fires, and the jump to frame 0 would be visible. Wait it
           out instead. */
        var rewindWhenHidden = function () {
          if (lastRatio > LEAVE_RATIO) {
            rewindTimer = setTimeout(rewindWhenHidden, REWIND_DELAY);
            return;
          }
          restart();
        };

        var runCaptions = function (on) {
          if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
          clearTimeout(rewindTimer);
          if (!on) {
            rewindTimer = setTimeout(rewindWhenHidden, REWIND_DELAY);
            return;
          }
          // Back before the rewind landed (a half-swipe, or straight back
          // up): do it now so the clip still starts from the top.
          if (stopped || video.currentTime > 0.05) restart();
          tick();
          rafId = requestAnimationFrame(pump);
        };

        if (nextBtn) {
          nextBtn.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            // goTo/current belong to the pager block further down. It bails
            // out early on desktop and under reduced motion, so they can be
            // undefined here — fall back to scrolling the next sheet in.
            if (typeof goTo === 'function') {
              goTo(current + 1, true);
              return;
            }
            var section = shell.closest('#MainContent > .shopify-section');
            var after = section && section.nextElementSibling;
            if (after && after.scrollIntoView) {
              after.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          });
        }

        var vo = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              var ratio = entry.intersectionRatio;
              lastRatio = ratio;
              if (!wanted && ratio >= ENTER_RATIO) {
                wanted = true;
                runCaptions(true);
                play();
              } else if (wanted && ratio <= LEAVE_RATIO) {
                wanted = false;
                runCaptions(false);
                video.pause();
              }
            });
          },
          { threshold: [0, LEAVE_RATIO, 0.25, ENTER_RATIO, 0.75, 0.99] }
        );
        vo.observe(video);

        // Coming back from a background tab / another app leaves the element
        // paused even though its sheet is still the one on screen.
        document.addEventListener('visibilitychange', function () {
          if (document.hidden) video.pause();
          else play();
        });
      });
    }

    /* ---------- Product card photo carousel: swipe + auto-advance --------- */
    if (isIndex) {
      var buildCarousel = function (box) {
        var slides = box.querySelectorAll('.ol-card-slide');
        if (!slides.length) return;
        var imgs = [];
        var firstImg = box.querySelector('img');
        if (firstImg && !firstImg.classList.contains('ol-card-slide')) {
          imgs.push(firstImg);
        }
        Array.prototype.push.apply(imgs, slides);
        var n = imgs.length;
        if (n < 2) return;

        box.classList.add('ol-carousel');

        // Every photo decodes to a live GPU layer up front — a photo's
        // first slide-in must be as smooth as its hundredth.
        imgs.forEach(function (img) {
          img.loading = 'eager';
          if (img.decode) {
            img.decode().catch(function () {});
          }
        });

        var dots = document.createElement('div');
        dots.className = 'ol-dots';
        var dotEls = [];
        for (var d = 0; d < n; d++) {
          dotEls.push(dots.appendChild(document.createElement('span')));
        }
        box.appendChild(dots);

        var pos = 0; // fractional photo index the strip is scrolled to
        var cur = []; // where each photo currently sits (rel units)
        // Photos only ever move on the compositor (translate on their own
        // GPU layers) — nothing scales, nothing re-clips, no layout. A calm,
        // slightly longer glide on the iOS sheet curve reads as premium.
        var GLIDE = 'transform 0.8s cubic-bezier(0.32, 0.72, 0, 1)';
        var place = function (i, rel, transition) {
          cur[i] = rel;
          imgs[i].style.transition = transition;
          imgs[i].style.transform = 'translate3d(' + rel * 100 + '%, 0, 0)';
        };
        var offStage = function (rel) {
          return Math.abs(rel) >= 1;
        };
        // dir: +1 when travelling forward (new photo enters from the right),
        // -1 when travelling back. Ties in the wrap (a 2-photo product, where
        // the other photo is both "next" and "previous") resolve to the exit
        // side so a photo never crosses the one it replaces.
        var render = function (animate, dir) {
          dir = dir < 0 ? -1 : 1;
          var glide = animate && !reduced;
          // Infinite loop: each photo renders at its nearest wrapped offset,
          // so "next" always enters from the right and "previous" from the
          // left — no rewind sweep across the whole strip.
          var rels = [];
          for (var i = 0; i < n; i++) {
            var rel = i - pos;
            rel -= n * Math.round(rel / n);
            if (n === 2 && Math.abs(rel) === 1) rel = -dir;
            rels.push(rel);
          }
          if (glide) {
            // Two-phase commit for a deterministic, identical glide every
            // cycle. Phase 1 (no transition): the arriving photo is staged
            // just off the entering edge if it isn't already partly on
            // screen, and off-stage photos teleport to their off-stage
            // slots. Flush. Phase 2: everything that still has to move
            // glides — arriving in, departing out, mid-drag settles.
            for (var a = 0; a < n; a++) {
              if (rels[a] === 0 && offStage(cur[a])) place(a, dir, 'none');
              else if (offStage(rels[a]) && offStage(cur[a])) place(a, rels[a], 'none');
            }
            void box.offsetWidth;
            for (var b = 0; b < n; b++) {
              if (cur[b] !== rels[b]) place(b, rels[b], GLIDE);
            }
          } else {
            for (var c = 0; c < n; c++) {
              place(c, rels[c], 'none');
            }
          }
          var active = ((Math.round(pos) % n) + n) % n;
          for (var j = 0; j < n; j++) {
            dotEls[j].classList.toggle('is-active', j === active);
          }
        };
        render(false);

        var timer = null;
        var stopAuto = function () {
          if (timer) {
            clearInterval(timer);
            timer = null;
          }
        };
        var playAuto = function () {
          stopAuto();
          if (reduced) return;
          timer = setInterval(function () {
            // Backgrounded tabs throttle animations — skip ticks there so
            // returning users never see a catch-up scramble
            if (document.hidden) return;
            pos = Math.round(pos) + 1;
            render(true, 1);
          }, 4000);
        };

        // Finger-follow swipe with rubber-banding and velocity flicks.
        // Vertical movement stays with the page-snap pager (it claims the
        // gesture itself once dy outweighs dx).
        var sX = 0;
        var sY = 0;
        var sPos = 0;
        var lastX = 0;
        var lastMoveT = 0;
        var velX = 0;
        var dragging = false;
        box.addEventListener(
          'touchstart',
          function (e) {
            sX = lastX = e.touches[0].clientX;
            sY = e.touches[0].clientY;
            sPos = pos;
            velX = 0;
            lastMoveT = e.timeStamp;
            dragging = false;
            stopAuto();
          },
          { passive: true }
        );
        box.addEventListener(
          'touchmove',
          function (e) {
            var dx = e.touches[0].clientX - sX;
            var dy = e.touches[0].clientY - sY;
            if (!dragging) {
              if (Math.abs(dx) < 6 || Math.abs(dy) > Math.abs(dx)) return;
              dragging = true;
            }
            var width = box.clientWidth || 1;
            // Endless in both directions — no rubber-band, the strip wraps
            var p = sPos - dx / width;
            velX = (e.touches[0].clientX - lastX) / Math.max(1, e.timeStamp - lastMoveT);
            lastX = e.touches[0].clientX;
            lastMoveT = e.timeStamp;
            pos = p;
            render(false, p >= sPos ? 1 : -1);
          },
          { passive: true }
        );
        box.addEventListener(
          'touchend',
          function () {
            if (dragging) {
              var next = Math.round(pos);
              if (velX < -0.3) next = Math.ceil(pos);
              else if (velX > 0.3) next = Math.floor(pos);
              var dir = next >= sPos ? 1 : -1;
              pos = next;
              render(true, dir);
            }
            playAuto();
          },
          { passive: true }
        );

        playAuto();
      };
      Array.prototype.forEach.call(
        document.querySelectorAll('#MainContent .card__media .media'),
        buildCarousel
      );
    }

    /* ---------- Find a Pro: open the store-locator overlay ----------
       The locator app renders a floating pill (.progus-store-locator-widget-handler)
       that our CSS hides; clicking it is what opens the overlay. The helpers
       below live outside any one entry point so the drawer link, the (legacy)
       header icon and the ?olfindpro=1 hook can all use them — they used to be
       trapped inside `if (findPro)`, and #FindAProIcon no longer exists in the
       theme, so none of it was running. */
    var locatorHandler = function () {
      return document.querySelector('.progus-store-locator-widget-handler');
    };
    var openLocator = function () {
      var handler = locatorHandler();
      if (handler) handler.click();
      return !!handler;
    };
    // Tapped before the widget script finished loading: keep trying briefly
    // and open it the moment it appears.
    var openLocatorSoon = function () {
      if (openLocator()) return;
      var tries = 0;
      var poll = setInterval(function () {
        if (openLocator() || ++tries > 40) clearInterval(poll);
      }, 250);
    };

    var findProIcon = document.getElementById('FindAProIcon');
    if (findProIcon) {
      // Show the pin immediately with the rest of the nav icons — never wait
      // for the async locator widget.
      findProIcon.hidden = false;
      findProIcon.addEventListener('click', openLocatorSoon);
    }

    // The drawer's FIND A PRO entry goes to the Find a Pro SHEET, not
    // straight into the overlay — see the sheet links in
    // snippets/header-drawer.liquid. ?olfindpro=1 still opens the overlay on
    // arrival, which is what the locator's own deep links use.
    if (new URLSearchParams(location.search).has('olfindpro')) {
      openLocatorSoon();
    }

    /* ---------- Legal sheet: the closing pitch ----------
       Runs ONCE per visit. It is held until the sheet is genuinely being
       looked at and then played a beat later, so it reads as a deliberate
       reveal rather than something that was already happening when you
       arrived — but swiping away and coming back finds it already there
       rather than replaying it, which is what a repeat performance would
       feel like. The observer disconnects the moment it fires, and the
       classes are never removed. */
    if (isIndex && 'IntersectionObserver' in window) {
      var legalCta = document.querySelector('[data-ol-legal-cta]');
      if (legalCta) {
        var ctaObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.intersectionRatio < 0.55) return;
              ctaObserver.disconnect();
              setTimeout(function () {
                legalCta.classList.add('is-in');
                /* The halo is lit as a separate, permanent state once the
                   box has come to rest (0.72s delay + 0.85s arrival).
                   Deliberately not part of any animation — a class with a
                   transition cannot expire or be dropped when the
                   compositor collapses the layer. */
                setTimeout(function () {
                  legalCta.classList.add('is-lit');
                  /* Last beat: the halo's bloom runs 2.6s from here, and the
                     title's shimmer follows it rather than competing with
                     it. One pass, then the sheet is simply what it is. */
                  setTimeout(function () {
                    legalCta.classList.add('is-shimmer');
                  }, 2700);
                }, 1600);
                // The sheen now runs 0.15s + 2.2s; the box still lands at
                // 0.72s + 0.85s, so the halo timing is unchanged.
              }, 700);
            });
          },
          { threshold: [0, 0.55] }
        );
        ctaObserver.observe(legalCta);
      }
    }

    /* ---------- Tracker sheet ----------
       The homepage carries the same ParcelPanel tracker as the TRACK YOUR
       ORDER page. All of that page's styling is scoped to its <body> id, so
       the sheet is given the alternate hook those rules also answer to
       (`:is(body#track-your-order, .ol-track-scope)`) rather than the
       ruleset being duplicated for it. */
    if (isIndex) {
      var trackerSheet = document.querySelector(
        '#MainContent > .shopify-section[id*="__tracker"]'
      );
      if (trackerSheet) trackerSheet.classList.add('ol-track-scope');
    }

    /* ---------- Nav wordmark belongs to the menu ----------
       On the homepage the bar carries only the hamburger and the cart; the
       Optimal mark appears when the menu is opened and goes when it closes.
       Read off the drawer's own state rather than a click handler, so it
       stays right however the drawer is opened or closed — including Dawn's
       own Escape and click-outside paths.

       `open` alone is the wrong signal on the way OUT. Dawn keeps the
       attribute for the whole 400ms its close animation runs and only then
       removes it, so the mark stood at full strength while the menu was
       already sliding away and started its fade once the drawer had gone —
       the pause is what read as a snap. `menu-opening` is the class Dawn
       adds and removes at the START of each animation, so requiring both
       puts the mark in step with the drawer in both directions. */
    if (isIndex) {
      var navDrawer = document.querySelector('header-drawer details');
      if (navDrawer) {
        var syncMenuLogo = function () {
          docEl.classList.toggle(
            'ol-menu-open',
            navDrawer.open && navDrawer.classList.contains('menu-opening')
          );
        };
        navDrawer.addEventListener('toggle', syncMenuLogo);
        if ('MutationObserver' in window) {
          new MutationObserver(syncMenuLogo).observe(navDrawer, {
            attributes: true,
            attributeFilter: ['open', 'class'],
          });
        }
        syncMenuLogo();
      }
    }

    /* ---------- Nav transparency ---------- */
    var headerWrapper = document.querySelector('.header-wrapper');
    var scrolledState = null;
    var setScrolledClass = function (on) {
      if (on === scrolledState) return; // avoid style writes on every touchmove
      scrolledState = on;
      docEl.classList.toggle('ol-scrolled', on);
      // Inline style as well — immune to any cascade/stacking surprises
      if (headerWrapper) {
        headerWrapper.style.backgroundColor = on ? '#000000' : 'transparent';
      }
    };
    if (isIndex && !(isMobile && !reduced)) {
      var onScroll = function () {
        setScrolledClass(window.scrollY > 24);
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    /* ---------- FAQ sheet: accordion -> tabs ----------
       The section is authored as a Dawn accordion so the questions stay
       editable in the theme editor; on the phone sheet it is presented as a
       pill per question that switches the answer in place. The pills are
       built from the summaries that are already there — nothing is
       hardcoded, so adding a row in the editor adds a pill. */
    if (isIndex && isMobile) {
      var faqArea = document.querySelector(
        '#MainContent > .shopify-section[id*="collapsible_content"] .accordion_area'
      );
      var faqRows = faqArea
        ? Array.prototype.slice.call(faqArea.querySelectorAll('.accordion'))
        : [];

      if (faqRows.length > 1) {
        var faqTabs = document.createElement('div');
        faqTabs.className = 'ol-faq-tabs';
        faqTabs.setAttribute('role', 'tablist');

        var faqSection = faqArea.closest('.shopify-section');
        /* Where the title parks for each tab. The title has to clear the tab
           that is standing up in the band beside it, so it moves one slot
           along as the selection moves: first tab open puts it in the
           middle, the middle tab pushes it right, the last sends it all the
           way left. Indexed modulo its own length, so a fourth row added in
           the editor carries the cycle on instead of falling off the end. */
        var FAQ_TITLE_SLOTS = ['center', 'end', 'start'];

        var faqButtons = [];
        var selectFaq = function (index) {
          if (faqSection) {
            faqSection.dataset.olFaqTitle = FAQ_TITLE_SLOTS[index % FAQ_TITLE_SLOTS.length];
          }
          faqRows.forEach(function (row, i) {
            row.classList.toggle('is-active', i === index);
          });
          faqButtons.forEach(function (button, i) {
            var on = i === index;
            button.classList.toggle('is-active', on);
            button.setAttribute('aria-selected', on ? 'true' : 'false');
            button.tabIndex = on ? 0 : -1;
            // Each tab's slanted edge overlaps the next one, so they have to
            // be stacked left-over-right — with the open tab in front of the
            // lot. Set here rather than in CSS because the count is however
            // many rows the editor holds.
            button.style.zIndex = on ? faqRows.length + 1 : faqRows.length - i;
          });
        };

        faqRows.forEach(function (row, i) {
          var details = row.querySelector('details');
          var title = row.querySelector('.accordion__title');
          // Panel visibility is the tab row's job now, so every details is
          // left open and its summary hidden in CSS.
          if (details) details.open = true;

          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'ol-faq-tab';
          button.setAttribute('role', 'tab');
          // The short label comes from the block's own `tab_label` setting so
          // it stays editable; the question is the fallback.
          var label = document.createElement('span');
          label.className = 'ol-faq-tab__label';
          label.textContent =
            row.getAttribute('data-ol-tab') ||
            (title ? title.textContent.trim() : 'Q' + (i + 1));
          button.appendChild(label);
          button.addEventListener('click', function () {
            selectFaq(i);
          });

          faqButtons.push(button);
          faqTabs.appendChild(button);
        });

        faqArea.parentNode.insertBefore(faqTabs, faqArea);
        faqArea.classList.add('ol-faq-panels');
        selectFaq(0);
      }
    }

    /* ---------- Hero BUY NOW -> the cart ----------
       Opens the cart, and stocks it with the kit first if the cart is empty
       — the kit is the whole shop, so a visitor who taps BUY NOW has already
       decided, and handing them an empty drawer wastes the decision.

       THE RULE HERE: this button never navigates. An earlier version fell
       back to the anchor's href whenever the cart round trip threw, which is
       how a tap could land on the collection page instead of the drawer.
       Every path below — a dead network, a variant that sold out between
       page load and the tap, a cart app that swallowed the drawer — ends at
       the cart. The worst case is an empty drawer, never a page load.

       The cart is opened through whatever is actually installed rather than
       by assuming Dawn's drawer: `renderContents` when the drawer is there
       (it renders the new cart AND opens itself, which is why the add asks
       for its sections), its own `open()` when there is nothing to render, a
       click on the header cart icon if another cart app owns the drawer and
       bound itself to that icon, and only then the /cart page. */
    var buyBtn = document.querySelector('[data-ol-buy]');
    if (buyBtn) {
      var cartRoot =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var buyBusy = false;

      var openCart = function () {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
          return;
        }
        var icon = document.getElementById('cart-icon-bubble');
        if (icon) {
          icon.click();
          return;
        }
        window.location.href = cartRoot + 'cart';
      };

      buyBtn.addEventListener('click', function (event) {
        event.preventDefault();
        /* Guards a double tap while the add is in flight — without it an
           impatient second tap puts a second kit in the cart. */
        if (buyBusy) return;
        buyBusy = true;

        fetch(cartRoot + 'cart.js', { credentials: 'same-origin' })
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            /* Something in it already: the drawer's markup was rendered with
               this cart at page load, so it is already right. */
            if (cart.item_count > 0) return null;

            var variant = buyBtn.getAttribute('data-ol-buy-variant');
            if (!variant) return null;

            /* Named literally, NOT mapped out of drawer.getSectionsToRender():
               this theme's CartDrawer returns {id, selector} entries with no
               `section` key, so mapping it asked the cart endpoint for two
               undefined sections. The add still succeeded, the response came
               back with nothing renderContents could use, it threw on the
               undefined HTML, and the catch below opened the drawer still
               showing the empty markup Liquid printed at page load — the
               "your cart is empty" on a cart that had just been filled.
               These two names are what Dawn's own add-to-cart flow asks for
               and what renderContents indexes the response by. */
            var body = {
              items: [{ id: Number(variant), quantity: 1 }],
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname
            };

            return fetch(cartRoot + 'cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body)
            }).then(function (response) {
              return response.json();
            });
          })
          .then(function (state) {
            var drawer = document.querySelector('cart-drawer');
            /* cart/add.js answers an error with a `status` field rather than
               by rejecting, so a sold-out variant arrives here looking like
               success. Either way the cart still opens.

               The `sections['cart-drawer']` test is the one that matters: an
               empty or malformed fragment is what renderContents chokes on,
               and opening the stale drawer is better than throwing. */
            if (
              state &&
              !state.status &&
              state.sections &&
              state.sections['cart-drawer'] &&
              drawer &&
              typeof drawer.renderContents === 'function'
            ) {
              drawer.renderContents(state);
            } else {
              openCart();
            }
          })
          .catch(openCart)
          .then(function () {
            buyBusy = false;
          });
      });
    }

    /* ---------- Mobile homepage pager ---------- */
    if (!isIndex || !isMobile || reduced) return;

    var track = document.getElementById('PageTrack');
    if (!track) return;

    var pages = Array.prototype.slice.call(
      document.querySelectorAll('#MainContent > .shopify-section')
    );
    /* The footer group is NOT a sheet on the homepage — the legal sheet is
       the last page in the stack. It is hidden in CSS in pager mode; leaving
       it out here is what stops the pager offering a page past the end. */
    if (pages.length < 2) return;

    docEl.classList.add('ol-pager');

    var CLEARANCE = 6; // resting gap between the nav bar and a sheet's top edge
    var GAP = 48; // gap under a sheet's rounded bottom so the curve (and a
    // peek of the next card) is always visible at rest (CLEARANCE + GAP = CSS 54px)

    var headerH = 0;
    var offsets = [];
    var current = 0;
    var translate = 0;

    // True visible height. iOS Safari reports the LARGE viewport (as if its
    // toolbars were collapsed) in both innerHeight and visualViewport — but
    // the pager freezes document scroll, so the toolbars never collapse and
    // the real visible area is the small viewport (100svh). Probe it and
    // trust the smallest credible value. Also subtract the Shopify
    // theme-preview bar overlay so previews match the live site.
    var svhProbe = document.createElement('div');
    svhProbe.style.cssText =
      'position:fixed;top:0;left:0;width:1px;height:100svh;visibility:hidden;pointer-events:none;';
    var viewportH = function () {
      var h = window.innerHeight;
      if (window.visualViewport) {
        h = Math.min(h, Math.round(window.visualViewport.height));
      }
      docEl.appendChild(svhProbe);
      var svh = svhProbe.offsetHeight; // 0 when the browser lacks svh support
      svhProbe.remove();
      if (svh > 0) h = Math.min(h, svh);
      var previewBar = document.getElementById('preview-bar-iframe');
      if (previewBar) {
        var r = previewBar.getBoundingClientRect();
        if (r.height > 0 && r.top < h) h -= r.height;
      }
      return h;
    };

    // Seed with the true height straight away — seeding from innerHeight
    // (the LARGE viewport on iOS) sized the hero a toolbar too tall for the
    // first frame, then it snapped shorter once measure() ran.
    var vh = viewportH();
    docEl.style.setProperty('--ol-vh', vh + 'px');

    var measure = function () {
      vh = viewportH();
      docEl.style.setProperty('--ol-vh', vh + 'px');
      var header = document.querySelector('.section-header');
      headerH = header ? header.offsetHeight : 0;
      offsets = pages.map(function (page, i) {
        return i === 0 ? 0 : Math.max(0, page.offsetTop - headerH - CLEARANCE);
      });
    };

    /* measure() reads offsetTop off every sheet and markScrollable() reads
       scrollHeight off every sheet — both force layout, and between goTo(),
       resettle() and the resize handler they were being run several times
       per swipe for a page that had not changed since the last read. They
       are now gated behind a dirty flag that only the things which can
       actually change a sheet's height set: the sheet ResizeObserver, a
       viewport resize, and load. */
    var layoutDirty = true;
    var remeasure = function (force) {
      if (!force && !layoutDirty) return;
      layoutDirty = false;
      measure();
      markScrollable();
    };

    /* Only the sheet on screen and its immediate neighbours are painted.
       The track is one promoted layer eight screens tall; with every sheet
       painted into it — maps, video, review carousels, masks and all — the
       compositor has to raster far more than it can hold, and re-rasters
       tiles as they come into view mid-swipe, which is what the jank is.
       `visibility: hidden` is deliberate rather than `display: none` or
       `content-visibility`: it keeps every sheet's box in layout, so the
       offsets stay valid, IntersectionObservers keep reporting, and the
       map and review widgets keep measuring a real size. The neighbour
       either side covers the peek at rest and the reveal mid-drag. */
    var PAINT_RADIUS = 1;
    /* A sheet carrying a <video> is never unpainted. Safari does not keep a
       video in a `visibility: hidden` subtree decoded, so coming back to it
       showed a black card while the element woke up and re-seeked — the
       clip appearing not to load at all. One always-live video layer is a
       cost worth paying; the map and the review carousels still drop out. */
    var alwaysPaint = pages.map(function (page) {
      return !!page.querySelector('video');
    });
    var paintWindow = function () {
      for (var i = 0; i < pages.length; i++) {
        pages[i].classList.toggle(
          'ol-far',
          !alwaysPaint[i] && Math.abs(i - current) > PAINT_RADIUS
        );
      }
    };

    var apply = function (value) {
      translate = value;
      // Nav turns black the moment the hero starts moving away, mid-drag
      setScrolledClass(value > 24);
      track.style.transform = 'translate3d(0, ' + -value + 'px, 0)';
    };

    var goTo = function (index, animate, velocity) {
      // Measure first, every time — but only actually re-read when
      // something has changed since the last read. Anything that changed a
      // sheet's height (the map mounting is the usual one) shifts every
      // sheet below it, and landing on a stale offset is what put the sheet
      // up under the nav bar.
      remeasure();
      current = Math.max(0, Math.min(pages.length - 1, index));
      if (animate) {
        /* The paint window is NOT widened here. Moving one page along
           reveals the sheet two away, and rastering a fresh full-screen
           sheet on the very frame the glide starts is a hitch you can feel
           at the top of every swipe. Everything the eye passes over on the
           way — the sheet being left and the one being travelled to — is
           already inside the current window, so the widening can wait until
           the glide has landed. */
        var dist = Math.abs(offsets[current] - translate);
        /* Duration follows the RELEASE, not the page. The old formula
           scaled with screen height and floored at half a second, so a
           finger that had already dragged 95% of the way still spent 500ms
           crawling the last 5% — the stack came unstuck from the hand at
           exactly the moment it should feel joined to it. Now the remaining
           distance is covered at roughly the speed the finger was going:
           a flick lands fast, a slow drag settles softly, and both are
           bounded so neither can feel abrupt or sluggish. */
        var speed = Math.max(0.5, Math.min(3.2, Math.abs(velocity || 0)));
        var duration = Math.max(0.2, Math.min(0.52, dist / speed / 1000));
        track.style.transitionDuration = duration.toFixed(3) + 's';
        track.classList.add('ol-animating');
        var done = function () {
          clearTimeout(animateGuard);
          track.classList.remove('ol-animating');
          track.style.transitionDuration = '';
          track.removeEventListener('transitionend', done);
          paintWindow();
        };
        track.addEventListener('transitionend', done);
        // transitionend does not fire if the transition is interrupted or
        // never starts; without this the guard class could stick and block
        // every later re-settle.
        var animateGuard = setTimeout(done, duration * 1000 + 120);
      } else {
        // Before the move, so the sheet being travelled to is already painted.
        paintWindow();
      }
      apply(offsets[current]);
    };

    // Sheets taller than their page scroll internally
    var markScrollable = function () {
      pages.forEach(function (page, i) {
        // Product sheet always snaps as one page — never scrolls internally
        if (page.id && page.id.indexOf('featured_collection') !== -1) {
          page.classList.remove('ol-scrollable');
          return;
        }
        // Same for any sheet that sizes itself to the screen. The reviews
        // sheet is measured before Judge.me mounts, when it is a different
        // height, so it was being left marked scrollable — which is what
        // let it be swiped inside instead of paging.
        if (page.querySelector('[data-ol-no-scroll]')) {
          page.classList.remove('ol-scrollable');
          return;
        }
        var max = i === 0 ? vh : vh - headerH - CLEARANCE - GAP;
        page.classList.toggle('ol-scrollable', page.scrollHeight > max + 4);
      });
    };

    /* ---------- Addressing a sheet by name ----------
       Sheets are found by the section key their id ends in
       (shopify-section-template--…__find_a_pro) rather than by position, so
       the menu can point at a sheet without anyone having to keep a page
       NUMBER in sync — adding, reordering or disabling a section can never
       silently send a link to the wrong sheet. The match is on the exact
       suffix: `__featured_collection` must not also answer for
       `__featured_collection_rTxf7G`. */
    var pageIndexOf = function (key) {
      if (!key) return -1;
      var suffix = '__' + key;
      for (var i = 0; i < pages.length; i++) {
        var id = pages[i].id || '';
        if (id.slice(-suffix.length) === suffix) return i;
      }
      return -1;
    };

    /* How the stack gets to a sheet the menu asked for.

       ONE shape for every entry, whatever the distance. Gliding to a
       near sheet and cutting to a far one meant the same gesture produced
       two different animations depending on where you happened to be
       standing, and gliding is the wrong answer anyway: seven sheets go
       past the eye inside a second, and since only the current sheet and
       its neighbours are painted, what actually goes by is a column of
       blank boxes. So: the sheet on screen drops out, the stack moves
       while nothing is showing, the sheet asked for settles in.

       Every jump takes over from the one before it rather than layering on
       top — the menu is a place people tap twice, and two runs of this
       sharing the same sheets is exactly where the stutter came from. */
    var JUMP_OUT = 140; // .ol-sheet-leave's duration; the timer is only a guard
    var jump = null; // the run in flight, if any

    /* Tears the current run down to nothing: no timers left to fire, no
       listeners left to catch, no classes left on either sheet. Every way
       out of a jump goes through here — it finished, another tap replaced
       it, or a finger landed on the screen — so a run can never leave a
       sheet holding a transform or a half-applied opacity. */
    var endJump = function () {
      if (!jump) return;
      clearTimeout(jump.moveGuard);
      clearTimeout(jump.settleGuard);
      jump.from.removeEventListener('animationend', jump.leave);
      jump.to.removeEventListener('animationend', jump.settle);
      jump.from.classList.remove('ol-leave');
      jump.to.classList.remove('ol-arrive');
      jump = null;
    };

    var jumpTo = function (index) {
      endJump();
      if (index === current) return;
      var from = pages[current];
      var to = pages[index];
      var run = { from: from, to: to };
      jump = run;

      var move = function (event) {
        /* animationend BUBBLES, and these sheets are full of their own
           animations — the FAQ panels, the closing pitch, the shimmer. Only
           this sheet's own fade is the cue to move. */
        if (event && (event.target !== from || event.animationName !== 'ol-sheet-leave')) return;
        if (jump !== run) return; // superseded while we were waiting
        clearTimeout(run.moveGuard);
        from.removeEventListener('animationend', run.leave);

        goTo(index, false);
        /* The sheet that left is off screen now, so its resting state can
           come back without anyone seeing it. */
        from.classList.remove('ol-leave');
        /* A sheet asked for by name is presented from its top, however far
           down it was left the last time it was read. */
        if (to.scrollTop) to.scrollTop = 0;
        /* The scroll-in class, in case that observer has not fired for this
           sheet yet — without it the sheet's resting opacity is 0 and it
           would blink out again the moment the entrance ends. */
        to.classList.add('ios-sheet-in');
        /* Restart the entrance from the top even if this sheet was already
           mid-arrival when the second tap came in. */
        to.classList.remove('ol-arrive');
        void to.offsetWidth;
        to.classList.add('ol-arrive');

        /* The class must come off: it holds a transform, and a transformed
           section is a containing block for anything `position: fixed`
           inside it — the locator overlay included. */
        run.settle = function (e) {
          if (e && (e.target !== to || e.animationName !== 'ol-sheet-arrive')) return;
          endJump();
        };
        run.settleGuard = setTimeout(run.settle, 700);
        to.addEventListener('animationend', run.settle);
      };

      run.leave = move;
      run.settle = function () {};
      /* Driven off the fade's own end rather than a matching timer: on a
         busy frame the two drift apart, and a timer that fires early moves
         the stack while the old sheet is still visible. The timer is kept
         purely as a backstop for an animation that never reports. */
      from.addEventListener('animationend', run.leave);
      run.moveGuard = setTimeout(move, JUMP_OUT + 120);
      from.classList.add('ol-leave');
    };

    /* Menu entries carry data-ol-sheet="<key>" alongside a real href. With
       the pager running we close the drawer and go to the sheet instead of
       reloading the homepage to land on it; anywhere the pager is off —
       desktop, reduced motion, another template — this listener never runs
       and the href does the job. Delegated, because the drawer's markup is
       Dawn's and gets re-rendered by the section editor. */
    document.addEventListener('click', function (event) {
      var target = event.target;
      var link = target && target.closest ? target.closest('[data-ol-sheet]') : null;
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button) return;
      var index = pageIndexOf(link.getAttribute('data-ol-sheet'));
      if (index < 0) return; // that sheet isn't here — let the link navigate
      event.preventDefault();
      if (index === current) {
        var open = link.closest('details');
        var close = open && open.querySelector('summary');
        if (close) close.click();
        return; // already here — just close the menu
      }
      var details = link.closest('details');
      var summary = details && details.querySelector('summary');
      if (summary) summary.click(); // Dawn's own close path
      /* Started immediately rather than after the drawer: the drawer is
         sliding off over the same moment, so the fade-out happens behind it
         and only the arrival is actually watched. */
      jumpTo(index);
    });

    /* The one way in from outside this scope. The cart drawer needs to send
       someone to the Find a Pro sheet from a control that is not a
       [data-ol-sheet] link — dimmed checkout buttons — and a full page load
       to ?olsheet= would throw away the pager's own animation to get there.
       Returns false when the sheet is not on this page so the caller can
       fall back to the href. */
    window.olGoToSheet = function (key) {
      var target = pageIndexOf(key);
      if (target < 0) return false;
      if (target !== current) jumpTo(target);
      return true;
    };

    requestAnimationFrame(function () {
      remeasure(true);
      // ?olsheet=<section key> lands on that sheet — what the menu links use
      // as their fallback when the pager isn't already running on this page.
      // ?olpage=N is the same jump by index (dev/testing hook).
      var params = new URLSearchParams(location.search);
      var named = pageIndexOf(params.get('olsheet'));
      var debugPage = parseInt(params.get('olpage'), 10);
      goTo(named >= 0 ? named : isNaN(debugPage) ? 0 : debugPage, false);
    });

    // The preview bar, web fonts, and late images can all change heights
    // after first paint — re-measure and re-settle (never mid-gesture).
    var resettleTimer = null;
    var resettle = function () {
      // Mid-gesture and mid-glide it must not yank the track — but dropping
      // the correction altogether means a height change that lands during a
      // swipe is never accounted for, and the stack stays off by that much
      // for good. Come back for it instead.
      if (axis !== null || track.classList.contains('ol-animating')) {
        clearTimeout(resettleTimer);
        resettleTimer = setTimeout(resettle, 140);
        return;
      }
      remeasure();
      if (translate !== offsets[current]) goTo(current, false);
    };
    window.addEventListener('load', function () {
      layoutDirty = true;
      resettle();
    });
    [400, 1200, 2500].forEach(function (delay) {
      setTimeout(resettle, delay);
    });

    // Offsets are read from offsetTop, so ANY sheet that changes height
    // after they were taken shifts every sheet below it and leaves the
    // stored figures stale — which is what lets the next sheet's top edge
    // peek above the fold. The fixed timers above miss whatever settles
    // later than 2.5s, and the map is exactly that on a cold load. Watching
    // the sheets themselves makes the stack self-correcting however late
    // the change lands. rAF-coalesced so a burst of layout (the map
    // mounting, a font swapping) costs one re-settle, and resettle() itself
    // bails mid-gesture, so it can never fight a swipe.
    if ('ResizeObserver' in window) {
      // Coalesced on a timer rather than requestAnimationFrame: rAF only
      // runs while the page is being painted, so a correction scheduled on
      // it can sit unexecuted whenever frames are not being produced — and
      // this is exactly a "nothing is moving yet" moment. A timeout fires
      // regardless.
      var sheetObserver = new ResizeObserver(function () {
        layoutDirty = true;
        clearTimeout(resettleTimer);
        resettleTimer = setTimeout(resettle, 60);
      });
      pages.forEach(function (page) {
        sheetObserver.observe(page);
      });
    }
    window.addEventListener('resize', function () {
      layoutDirty = true;
      remeasure(true);
      goTo(current, false);
    });

    var startY = 0;
    var startX = 0;
    var startT = 0;
    var startTranslate = 0;
    var lastY = 0;
    var lastT = 0;
    /* Throw speed, measured over the last VELOCITY_WINDOW ms of movement.

       It CANNOT be taken at release: iOS reports touchend at the same
       coordinates and the same millisecond as the final touchmove, so the
       move-to-end delta is zero almost every time — which meant the flick
       threshold below never fired at all, and only a 60px displacement
       could ever turn a page.

       A window rather than a smoothed running figure: a single stale frame
       at the end of the gesture — a duplicate sample, a coalesced move —
       drags an exponential average most of the way to zero right when it is
       about to be read, so a genuine flick could still be scored as a rest.
       Averaging over a window is what the platforms themselves do, and it
       cannot be spoiled by one bad sample. */
    var VELOCITY_WINDOW = 90; // ms
    var sampleY = [];
    var sampleT = [];
    var axis = null; // 'y' | 'x' | 'skip'
    var dragRaf = null;
    var dragTarget = 0;

    var inNativeUI = function (el) {
      if (!el.closest) return false;
      // While the locator map is unlocked the sheet stops paging entirely —
      // any drag pans the map, and SEARCH HERE is the only way out. Matching
      // the whole sheet rather than just the map element means a drag that
      // starts on the title strip cannot swipe away either.
      if (el.closest('.ol-pro--map-live')) return true;
      return !!el.closest(
        'cart-drawer, header-drawer, modal-dialog, dialog, details-modal, .quick-add-modal'
      );
    };

    /* If the gesture starts inside ANY scrollable box that can still move in
       the swipe direction, let it scroll natively instead of paging.

       This used to look only for `.ol-scrollable`, the class put on whole
       sections — so a list nested inside a sheet (the Find a Pro rail) was
       invisible to it and every drag paged away instead of scrolling. Walking
       the ancestors and reading the computed overflow covers both.

       The WALK happens at touch-down, not on the move that decides the axis.
       getComputedStyle on every ancestor forces the style engine to resolve
       there and then, and doing it on the first moving frame delays the very
       first pixel of follow — the part of a swipe that is felt most. A touch
       event's target never changes for the life of the touch, so the chain
       found at touch-down is the same chain the move would have walked, and
       at touch-down there is nothing else competing for the frame. */
    var scrollChain = [];
    var collectScrollChain = function (el) {
      var chain = [];
      var node = el;
      while (node && node !== document.body && node.nodeType === 1) {
        var oy = getComputedStyle(node).overflowY;
        if (oy === 'auto' || oy === 'scroll') chain.push(node);
        node = node.parentElement;
      }
      return chain;
    };
    // Only scrollTop/scrollHeight from here on: layout reads, no style work.
    var innerScrollWins = function (dy) {
      for (var i = 0; i < scrollChain.length; i++) {
        var node = scrollChain[i];
        if (node.scrollHeight <= node.clientHeight + 1) continue;
        if (dy < 0) {
          if (node.scrollTop + node.clientHeight < node.scrollHeight - 1) return true;
        } else if (node.scrollTop > 0) {
          return true;
        }
      }
      return false;
    };

    document.addEventListener(
      'touchstart',
      function (e) {
        // Interruptible glide: catching a sheet mid-flight freezes it under
        // the finger instead of locking out the gesture (native iOS feel)
        if (track.classList.contains('ol-animating')) {
          var matrix = getComputedStyle(track).transform;
          track.classList.remove('ol-animating');
          track.style.transitionDuration = '';
          if (matrix && matrix !== 'none') {
            apply(-new DOMMatrixReadOnly(matrix).m42);
          }
        }
        /* A finger on the screen outranks a menu jump: whatever the menu
           asked for, the user is driving now. Without this the pending move
           fired mid-gesture and yanked the stack to the menu's destination
           under their thumb. */
        endJump();
        /* A second finger is never a page swipe — it is a pinch. Without
           this the handler re-armed on the extra touchstart, and lifting one
           finger mid-pinch left the other free to page the sheet away. */
        if (e.touches.length > 1) {
          axis = 'skip';
          return;
        }
        axis = null;
        sampleY.length = 0;
        sampleT.length = 0;
        startY = lastY = e.touches[0].clientY;
        startX = e.touches[0].clientX;
        startT = lastT = e.timeStamp;
        startTranslate = translate;
        if (inNativeUI(e.target)) {
          axis = 'skip';
          scrollChain = [];
          return;
        }
        scrollChain = collectScrollChain(e.target);
      },
      { passive: true }
    );

    document.addEventListener(
      'touchmove',
      function (e) {
        if (axis === 'skip' || axis === 'x') return;
        var y = e.touches[0].clientY;
        var dy = y - startY;
        var dx = e.touches[0].clientX - startX;

        if (e.touches.length > 1) {
          axis = 'skip';
          return;
        }

        if (axis === null) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          if (Math.abs(dx) > Math.abs(dy)) {
            axis = 'x'; // horizontal — leave carousels alone
            return;
          }
          // Re-checked here, not just on touchstart: axis is reset whenever a
          // finger goes down or up, so a gesture that began on a locked
          // surface could otherwise be re-classified as a page swipe.
          if (inNativeUI(e.target)) {
            axis = 'skip';
            return;
          }
          if (innerScrollWins(dy)) {
            axis = 'skip'; // native inner scroll
            return;
          }
          axis = 'y';
        }

        e.preventDefault();

        // Finger-follow with rubber-band resistance past the ends.
        // rAF-batched: touch events can outpace frames — write once per frame.
        var target = startTranslate - dy;
        var min = offsets[0];
        var max = offsets[offsets.length - 1];
        if (target < min) target = min - (min - target) * 0.3;
        if (target > max) target = max + (target - max) * 0.3;
        dragTarget = target;
        if (!dragRaf) {
          dragRaf = requestAnimationFrame(function () {
            dragRaf = null;
            apply(dragTarget);
          });
        }

        sampleY.push(y);
        sampleT.push(e.timeStamp);
        while (sampleT.length > 2 && e.timeStamp - sampleT[0] > VELOCITY_WINDOW) {
          sampleY.shift();
          sampleT.shift();
        }

        lastY = y;
        lastT = e.timeStamp;
      },
      { passive: false }
    );

    document.addEventListener(
      'touchend',
      function (e) {
        // Still fingers down — the gesture is not over, so leave axis alone.
        if (e.touches.length > 0) return;
        if (axis !== 'y') {
          axis = null;
          return;
        }
        axis = null;
        if (dragRaf) {
          cancelAnimationFrame(dragRaf);
          dragRaf = null;
          apply(dragTarget);
        }
        var dy = e.changedTouches[0].clientY - startY;
        /* px/ms over the final window. A finger that came to rest before
           lifting is placing the sheet, not throwing it — no move has
           landed inside the window, so it scores zero and the release
           settles softly from wherever it was let go. */
        var velocity = 0;
        var n = sampleT.length;
        if (n > 1 && e.timeStamp - sampleT[n - 1] <= VELOCITY_WINDOW) {
          velocity = (sampleY[n - 1] - sampleY[0]) / Math.max(1, sampleT[n - 1] - sampleT[0]);
        }

        var next = current;
        if (dy < -60 || velocity < -0.35) next = current + 1;
        else if (dy > 60 || velocity > 0.35) next = current - 1;
        goTo(next, true, velocity);
      },
      { passive: true }
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();

/* ---------- Track Your Order: minimum loading time ----------
   ParcelPanel can return a cached result in well under a second, which makes
   the spinner flash and the page look glitchy. Hold the loading state for at
   least MIN_MS from the moment it starts, however fast the data arrives.

   The app removes its own `.loading-container` as soon as it renders the
   result, so we can't just style theirs — we mount our own loader and keep
   `html.ol-track-hold` set until the minimum has elapsed. The CSS hides the
   form, the app's loader and the result while that class is present. */
(function () {
  var MIN_MS = 1500;

  var start = function () {
    if (!document.body || document.body.id !== 'track-your-order') return;
    var app = document.getElementById('pp-tracking-page-app');
    if (!app || !window.MutationObserver) return;

    var startedAt = 0;
    var holdTimer = null;
    var loader = null;

    var mount = function () {
      if (loader) return;
      loader = document.createElement('div');
      loader.className = 'ol-track-loader';
      loader.innerHTML =
        '<div class="ol-track-loader__ring"></div>' +
        '<div class="ol-track-loader__label">LOCATING YOUR SHIPMENT</div>';
      (app.querySelector('.pp_tracking_content') || app).appendChild(loader);
      document.documentElement.classList.add('ol-track-hold');
    };

    var unmount = function () {
      document.documentElement.classList.remove('ol-track-hold');
      if (loader && loader.parentNode) loader.parentNode.removeChild(loader);
      loader = null;
      startedAt = 0;
      holdTimer = null;
    };

    var observer = new MutationObserver(function () {
      // loading began
      if (!startedAt && app.querySelector('.loading-container')) {
        startedAt = Date.now();
        mount();
      }
      // result is in — reveal it, but not before the minimum has passed
      if (startedAt && !holdTimer && app.querySelector('.pp_tracking_result .timeline-item')) {
        var remaining = Math.max(0, MIN_MS - (Date.now() - startedAt));
        holdTimer = setTimeout(unmount, remaining);
      }
    });

    observer.observe(app, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* ---------- Reviews sheet: Judge.me carousel polish ----------
   Two jobs, both of which have to wait for Judge.me to hydrate its widget:

   1. The carousel ships the customer's own photo as a 70x70 (sometimes
      140x140) thumbnail. Shown as the hero of the card that is a blurry
      upscale, so the Shopify CDN size suffix is rewritten to something big
      enough. Shopify never upscales past the original, so asking for 1200
      is safe for small uploads.
   2. Replay the card's entrance animation whenever the carousel lands on a
      new review. Judge.me gives no "slide changed" event, so this watches
      the wrapper's inline transform, which is what it moves.            */
(function () {
  if (!document.querySelector('[data-ol-reviews-sheet]')) return;

  var SIZE = '_1200x1200';
  var SUFFIX = /_(\d+)x(\d+)(?=\.(?:jpe?g|png|webp|gif)\b)/i;

  var upscale = function (url) {
    if (!url) return url;
    if (SUFFIX.test(url)) return url.replace(SUFFIX, SIZE);
    // No size suffix means the original is already being served — leave it.
    return url;
  };

  var applyUpscale = function (img) {
    var current = img.getAttribute('src') || '';
    if (current.indexOf(SIZE) !== -1) return; // already ours
    var next = upscale(current || img.getAttribute('data-src'));
    if (!next || next === current) return;
    // srcset would otherwise win over the src we just set.
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.setAttribute('src', next);
    img.setAttribute('decoding', 'async');
  };

  /* ---------- The customer's photo, not the product shot ----------
     Judge.me puts the product picture on every carousel card, so a row of
     six reviews shows the same box six times. The photo the reviewer took is
     the reason anyone stops, and it is not in the carousel markup: it is in
     the product review widget, rendered hidden beside the sheet by
     apps.liquid purely as a lookup table.

     Read once into review id -> photo url, then the table is deleted so
     Judge.me's own script never finds a second widget to initialise. A
     review with no photo of its own is left alone and keeps showing the
     product shot. */
  var reviewPhotos = null;

  /* The widget asks imgix for a 160px thumbnail, because in the widget that
     is all it needs. On this sheet the same picture fills a card 709 device
     pixels wide, so used as given it is blown up more than four times and
     looks soft. Judge.me serves through imgix, so the size is a query
     parameter: strip whatever dimensions came down and ask for the size the
     card actually renders. 1200 matches what the product shot is upscaled
     to, so the two look like they belong to the same row. */
  var PHOTO_W = 1200;

  var sizeUp = function (url) {
    var q = url.indexOf('?');
    var base = q === -1 ? url : url.slice(0, q);
    // Only theirs. Anything else is left exactly as it arrived.
    if (base.indexOf('imgix') === -1) return url;
    var params = [];
    if (q !== -1) {
      var parts = url.slice(q + 1).split('&');
      for (var k = 0; k < parts.length; k++) {
        var name = parts[k].split('=')[0];
        if (parts[k] && name !== 'w' && name !== 'h' && name !== 'dpr') params.push(parts[k]);
      }
    }
    if (params.indexOf('auto=format') === -1) params.push('auto=format');
    params.push('w=' + PHOTO_W);
    return base + '?' + params.join('&');
  };

  var loadReviewPhotos = function () {
    if (reviewPhotos) return reviewPhotos;
    reviewPhotos = {};
    var table = document.querySelector('[data-ol-review-photos]');
    if (!table) return reviewPhotos;
    var cards = table.querySelectorAll('.jdgm-rev[data-review-id]');
    for (var i = 0; i < cards.length; i++) {
      var id = cards[i].getAttribute('data-review-id');
      var img = cards[i].querySelector('.jdgm-rev__pics img, .jdgm-rev__pic-img');
      /* Theirs are lazy, so the real URL is on data-src until their script
         runs; inside a hidden block it never will. */
      var url = img && (img.getAttribute('data-src') || img.getAttribute('src'));
      if (id && url) reviewPhotos[id] = sizeUp(url);
    }
    table.parentNode.removeChild(table);
    return reviewPhotos;
  };

  var applyReviewPhotos = function (root) {
    var map = loadReviewPhotos();
    var items = root.querySelectorAll('.jdgm-carousel-item[data-review-id]');
    for (var i = 0; i < items.length; i++) {
      var id = items[i].getAttribute('data-review-id');
      var url = map[id];
      if (!url) continue; // no photo of their own: the product shot stays
      var img = items[i].querySelector('.jdgm-carousel-item__product-image');
      if (!img || img.getAttribute('data-ol-photo') === url) continue;
      img.setAttribute('data-ol-photo', url);
      /* Both have to go or they win over the src set below. */
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.removeAttribute('data-src-retina');
      img.setAttribute('src', url);
      img.setAttribute('decoding', 'async');
      /* Judge.me swaps its own retina product image in after first paint,
         which would put the box back. Put the photo back after it. */
      if (!img.dataset.olPhotoWatched && window.MutationObserver) {
        img.dataset.olPhotoWatched = '1';
        new MutationObserver(function (records) {
          var el = records[0].target;
          var want = el.getAttribute('data-ol-photo');
          if (want && el.getAttribute('src') !== want) el.setAttribute('src', want);
        }).observe(img, { attributes: true, attributeFilter: ['src'] });
      }
    }
  };

  var upscaleImages = function (root) {
    var imgs = root.querySelectorAll('.jdgm-carousel-item__product-image');
    Array.prototype.forEach.call(imgs, function (img) {
      applyUpscale(img);
      if (img.dataset.olWatched) return;
      img.dataset.olWatched = '1';
      // Judge.me swaps the thumbnail for a 2x variant of its own AFTER the
      // first paint, which would undo this. Watch src and put it back. The
      // guard above makes the re-entry a no-op, so it cannot loop.
      new MutationObserver(function () {
        applyUpscale(img);
      }).observe(img, { attributes: true, attributeFilter: ['src', 'srcset'] });
    });
  };

  var replayEntrance = function (root) {
    var items = root.querySelectorAll('.jdgm-carousel-item');
    Array.prototype.forEach.call(items, function (item) {
      item.classList.remove('ol-review-in');
    });
    // One reflow for the batch, then re-add — without it the removal and
    // the re-add collapse into a single style pass and nothing replays.
    void root.offsetWidth;
    Array.prototype.forEach.call(items, function (item) {
      item.classList.add('ol-review-in');
    });
  };

  var attach = function (sheet) {
    var wrapper = sheet.querySelector('.jdgm-carousel__item-wrapper');
    if (!wrapper) return false;

    upscaleImages(sheet);
    applyReviewPhotos(sheet);
    replayEntrance(sheet);

    /* Stars belong on the byline, not on a row of their own above the title.
       Judge.me renders them inside .jdgm-carousel-item__review, a different
       parent from the name, so `order` cannot do it — the node is moved. The
       row it vacates goes to the photo. */
    Array.prototype.forEach.call(
      sheet.querySelectorAll('.jdgm-carousel-item'),
      function (item) {
        var stars = item.querySelector('.jdgm-carousel-item__review-rating');
        var byline = item.querySelector('.jdgm-carousel-item__reviewer-name-wrapper');
        if (stars && byline && stars.parentNode !== byline) {
          byline.insertBefore(stars, byline.firstChild);
        }
      }
    );

    var arrows = sheet.querySelector('.jdgm-carousel__arrows');
    var left = sheet.querySelector('.jdgm-carousel__left-arrow');
    var right = sheet.querySelector('.jdgm-carousel__right-arrow');
    var items = sheet.querySelectorAll('.jdgm-carousel-item');

    // Which review is showing, read back off the wrapper's own transform —
    // Judge.me exposes no index.
    var activeIndex = function () {
      var m = /translate3d\(\s*(-?[\d.]+)px/.exec(wrapper.style.transform || '');
      if (!m || !items.length) return 0;
      var step = items[0].getBoundingClientRect().width;
      if (!step) return 0;
      return Math.max(0, Math.min(items.length - 1, Math.round(-parseFloat(m[1]) / step)));
    };

    /* ---- Dots ----
       Judge.me ships arrows and an empty aria-live region, so there is no
       indication of how many reviews there are or where you are in them. */
    var dots = null;
    if (arrows && items.length > 1) {
      dots = document.createElement('div');
      dots.className = 'ol-review-dots';
      for (var i = 0; i < items.length; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ol-review-dot';
        dot.setAttribute('aria-label', 'Review ' + (i + 1) + ' of ' + items.length);
        dot.dataset.index = String(i);
        dots.appendChild(dot);
      }
      // Between the two arrows.
      if (right) arrows.insertBefore(dots, right);
      else arrows.appendChild(dots);

      dots.addEventListener('click', function (event) {
        var dot = event.target.closest('.ol-review-dot');
        if (!dot) return;
        // No "go to index" API, so step with the arrows. At six reviews
        // that is three taps at worst and reads as a deliberate scroll.
        var target = parseInt(dot.dataset.index, 10);
        var step = function () {
          var now = activeIndex();
          if (now === target) return;
          var button = now < target ? right : left;
          if (!button) return;
          button.click();
          setTimeout(step, 380);
        };
        step();
      });
    }

    var syncDots = function () {
      if (!dots) return;
      var now = activeIndex();
      Array.prototype.forEach.call(dots.children, function (dot, i) {
        dot.classList.toggle('is-active', i === now);
      });
    };
    syncDots();

    /* ---- Aggregate rating ----
       Judge.me renders the score as five star glyphs and the count as
       "from 6 reviews" on a line of its own. The exact figure is on the
       element as data-score, so it can be shown as a number rather than
       left for the eye to count. */
    var rating = sheet.querySelector('.jdgm-all-reviews-rating');
    if (rating && !sheet.querySelector('.ol-review-score')) {
      var score = parseFloat(rating.getAttribute('data-score'));
      if (!isNaN(score)) {
        var scoreEl = document.createElement('span');
        scoreEl.className = 'ol-review-score';
        scoreEl.textContent = score.toFixed(1);
        rating.parentNode.insertBefore(scoreEl, rating.nextSibling);
      }
    }

    /* ---- Swipe ----
       The carousel has no touch handling of its own — verified by dispatching
       a drag at it and watching the transform not move. Horizontal only, so
       the vertical sheet pager keeps every up/down gesture. */
    var container = sheet.querySelector('.jdgm-carousel__item-container');
    if (container && (left || right)) {
      var x0 = 0;
      var y0 = 0;
      var tracking = false;

      container.addEventListener(
        'touchstart',
        function (e) {
          if (e.touches.length !== 1) return;
          x0 = e.touches[0].clientX;
          y0 = e.touches[0].clientY;
          tracking = true;
        },
        { passive: true }
      );

      container.addEventListener(
        'touchend',
        function (e) {
          if (!tracking) return;
          tracking = false;
          var t = e.changedTouches[0];
          var dx = t.clientX - x0;
          var dy = t.clientY - y0;
          // Must be decisively horizontal or the pager's swipe wins.
          if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
          var button = dx < 0 ? right : left;
          if (button) button.click();
        },
        { passive: true }
      );
    }

    var lastTransform = wrapper.style.transform;
    new MutationObserver(function () {
      if (wrapper.style.transform === lastTransform) return;
      lastTransform = wrapper.style.transform;
      upscaleImages(sheet); // lazy slides get their src filled in late
      applyReviewPhotos(sheet);
      replayEntrance(sheet);
      syncDots();
    }).observe(wrapper, { attributes: true, attributeFilter: ['style'] });

    return true;
  };

  /* ---------- A tap on a review goes to the REVIEWS page ----------
     Judge.me makes only the product strip at the foot of each card a link,
     and points it at that product's own review anchor. Neither is what this
     sheet is for: the card is a taste of the reviews, and the place it
     should lead is the same REVIEWS page the hamburger opens.

     Delegated from the sheet and taken in the CAPTURE phase, so it runs
     before Judge.me's own handlers and before the anchor's default — and so
     it keeps working across the re-renders the widget does as slides load,
     which a listener bound to the anchors themselves would not survive.

     The whole card is the target, not just the strip: a tap on the words is
     the tap most people make.

     The href is read off the hamburger's own REVIEWS link so the two can
     never drift apart if the page handle changes; the literal is only a
     fallback for a page where the drawer is not rendered. */
  var reviewsHref = function () {
    var link = document.querySelector('.menu-drawer__menu-item[href*="/pages/reviews"]');
    return (link && link.getAttribute('href')) || '/pages/reviews';
  };

  var wireCardTaps = function (sheet) {
    if (sheet.dataset.olReviewTap) return;
    sheet.dataset.olReviewTap = '1';

    /* A swipe across the carousel ends in a click too. Anything that moved
       more than a thumb's wobble is a drag, and a drag is not a tap. */
    var DRAG = 10;
    var downX = 0;
    var downY = 0;
    sheet.addEventListener(
      'pointerdown',
      function (event) {
        downX = event.clientX;
        downY = event.clientY;
      },
      true
    );

    sheet.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        var card = target && target.closest ? target.closest('.jdgm-carousel-item') : null;
        if (!card) return;
        if (Math.abs(event.clientX - downX) > DRAG || Math.abs(event.clientY - downY) > DRAG) {
          return;
        }
        /* The arrows and the dots live outside a card, so they are already
           excluded by the closest() above and keep working. */
        event.preventDefault();
        event.stopPropagation();
        window.location.href = reviewsHref();
      },
      true
    );
  };

  var start = function () {
    var sheet = document.querySelector('[data-ol-reviews-sheet]');
    if (!sheet) return;
    wireCardTaps(sheet);
    if (attach(sheet)) return;
    // The widget mounts asynchronously; give it a while before giving up.
    var tries = 0;
    var poll = setInterval(function () {
      if (attach(sheet) || ++tries > 60) clearInterval(poll);
    }, 250);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* ---------- Find a Pro: search, locate, sort ----------
   Everything is client-side against the cards Liquid already rendered, so
   there is no API key, no map billing and no request on keystroke. Two ways
   in, because a dealer without coordinates still has to be findable:

     - typing filters on a `data-search` haystack (name, street, city,
       state, ZIP) built server-side;
     - "use my location" sorts by real distance for every dealer that has
       latitude/longitude, and leaves the rest below them in original order.

   Distance is haversine — good to a few metres at these ranges, and far
   cheaper than asking a geocoding service.                              */
(function () {
  var root = document.querySelector('[data-ol-pro]');
  if (!root) return;

  var list = root.querySelector('[data-ol-pro-list]');
  var search = root.querySelector('[data-ol-pro-search]');
  var locate = root.querySelector('[data-ol-pro-locate]');
  var status = root.querySelector('[data-ol-pro-status]');
  var count = root.querySelector('[data-ol-pro-count]');
  var empty = root.querySelector('[data-ol-pro-empty]');

  /* No early return on an empty list. The map, the search field and the
     dealer CTA all have to work on a sheet with zero dealers — bailing here
     is what left the map dead while the network was still being built. */
  var cards = list
    ? Array.prototype.slice.call(list.querySelectorAll('[data-ol-pro-card]'))
    : [];

  // Original order is the fallback ordering, and what we return to if the
  // visitor never shares a location.
  cards.forEach(function (card, i) {
    card.dataset.order = String(i);
  });

  /* How many tiles fit is a function of how TALL they are, not how many
     there are. A fixed "first six" cap assumed every tile was the same
     height; a ZIP search that turns up dealers with long names and
     three-line addresses makes the rows deep enough that six overflow the
     rail, and the list's `overflow: hidden` slices the last row. Measure
     the slot and trim to it instead. Called after anything that changes
     what is in the list, how it is ordered, or how big the rail is. */
  var capList = function () {
    if (!list) return;
    var visible = cards.filter(function (card) {
      card.classList.remove('ol-pro__card--over');
      return !card.hidden;
    });
    // Not laid out yet (or the rail is closed) — measuring now would trim
    // against a zero-height box and hide everything.
    if (!visible.length || list.clientHeight < 40) return;
    for (var i = visible.length - 1; i > 0; i--) {
      if (list.scrollHeight <= list.clientHeight + 1) break;
      visible[i].classList.add('ol-pro__card--over');
    }
  };

  var setStatus = function (text) {
    if (!status) return;
    status.textContent = text || '';
    status.hidden = !text;
  };

  /* "NEAR 22201" — the label plain, the postcode in the accent. Built from
     nodes rather than innerHTML so the value from the geocoder is inserted
     as text and can never be markup. */
  var setStatusNear = function (zip) {
    if (!status) return;
    status.textContent = '';
    status.appendChild(document.createTextNode('NEAR '));
    var el = document.createElement('span');
    el.className = 'ol-pro__status-zip';
    el.textContent = zip;
    status.appendChild(el);
    status.hidden = false;
  };

  /* Turn the visitor's coordinates into a postcode with the same Mapbox
     token the map uses. Falls back silently to the generic label — the sort
     order is already correct either way, this is only how it is described. */
  var showZip = function (lat, lng) {
    if (!token || !window.fetch) return;
    fetch(
      'https://api.mapbox.com/search/geocode/v6/reverse?longitude=' +
        encodeURIComponent(lng) +
        '&latitude=' +
        encodeURIComponent(lat) +
        '&types=postcode&limit=1&access_token=' +
        encodeURIComponent(token)
    )
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        var f = data && data.features && data.features[0];
        var zip = f && f.properties && f.properties.name;
        // Postcodes only — anything else means the lookup missed.
        if (zip && /^[A-Za-z0-9 -]{3,10}$/.test(zip)) setStatusNear(zip);
      })
      .catch(function () {});
  };

  var applyFilter = function () {
    var q = (search && search.value ? search.value : '').trim().toLowerCase();
    var shown = 0;
    cards.forEach(function (card) {
      var hit = !q || (card.dataset.search || '').indexOf(q) !== -1;
      card.hidden = !hit;
      if (hit) shown++;
    });
    if (empty) empty.hidden = shown !== 0;
    if (count) {
      count.textContent = shown + ' INSTALLER' + (shown === 1 ? '' : 'S');
    }
    syncRail(shown);
    capList();
  };

  if (search) {
    search.addEventListener('input', applyFilter);
    // A search input's clear button fires `search`, not `input`, in Safari.
    search.addEventListener('search', applyFilter);
  }

  var toRad = function (deg) {
    return (deg * Math.PI) / 180;
  };

  var distanceMiles = function (lat1, lng1, lat2, lng2) {
    var R = 3958.8; // earth radius, miles
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  /* opts.marker  - drop the "you" pin (false for a map-centre search)
     opts.frame   - re-frame the map (false keeps the visitor's own view) */
  var sortByDistance = function (lat, lng, opts) {
    opts = opts || {};
    var withCoords = 0;

    cards.forEach(function (card) {
      var dLat = parseFloat(card.dataset.lat);
      var dLng = parseFloat(card.dataset.lng);
      var label = card.querySelector('[data-ol-pro-distance]');
      if (isNaN(dLat) || isNaN(dLng)) {
        card.dataset.distance = '';
        if (label) label.hidden = true;
        return;
      }
      var miles = distanceMiles(lat, lng, dLat, dLng);
      card.dataset.distance = String(miles);
      withCoords++;
      if (label) {
        label.textContent = (miles < 10 ? miles.toFixed(1) : Math.round(miles)) + ' MI AWAY';
        label.hidden = false;
      }
    });

    // Dealers with coordinates first, nearest to furthest; the rest keep
    // their original order underneath rather than being dropped.
    var sorted = cards.slice().sort(function (a, b) {
      var da = a.dataset.distance === '' ? Infinity : parseFloat(a.dataset.distance);
      var db = b.dataset.distance === '' ? Infinity : parseFloat(b.dataset.distance);
      if (da === db) return Number(a.dataset.order) - Number(b.dataset.order);
      return da - db;
    });
    sorted.forEach(function (card) {
      list.appendChild(card);
    });
    capList();

    // Short enough to sit on one line beside the count. Replaced by
    // "NEAR <zip>" as soon as the reverse lookup answers.
    setStatus(withCoords ? 'NEAREST FIRST' : 'NO COORDINATES YET');
    if (withCoords) showZip(lat, lng);

    if (map && window.mapboxgl) {
      // Drop a marker for the visitor and frame it with the nearest dealer.
      var mapboxgl = window.mapboxgl;
      if (opts.marker !== false) {
        if (youMarker) youMarker.remove();
        var youEl = document.createElement('span');
        youEl.className = 'ol-pro__pin ol-pro__pin--you';
        youEl.innerHTML = '<span class="ol-pro__pin-core"></span>';
        youMarker = new mapboxgl.Marker({ element: youEl, anchor: 'center' })
          .setLngLat([lng, lat])
          .addTo(map);
      }

      if (opts.frame === false) return;

      var nearest = null;
      for (var i = 0; i < sorted.length; i++) {
        if (sorted[i].dataset.distance !== '') {
          nearest = sorted[i];
          break;
        }
      }
      if (nearest) {
        var b = new mapboxgl.LngLatBounds([lng, lat], [lng, lat]);
        b.extend([parseFloat(nearest.dataset.lng), parseFloat(nearest.dataset.lat)]);
        map.fitBounds(b, { padding: { top: 90, bottom: 320, left: 50, right: 50 }, maxZoom: 12 });
      } else {
        map.easeTo({ center: [lng, lat], zoom: 10 });
      }
    }
  };

  /* ---- Map ----
     Mapbox GL JS on the same account, token and night style the Optimal app
     uses, so the locator on the site and the map in the app are the same
     product. Library and CSS are injected on demand: this is the last sheet
     in the pager and most visitors never reach it.

     A previous pass used Leaflet on CARTO, which now stamps "API KEY
     REQUIRED" across every tile, then Leaflet on raw OpenStreetMap, whose
     tiles had to be inverted in CSS and were never going to look like the
     app. Both are gone. */
  var GL_JS = 'https://api.mapbox.com/mapbox-gl-js/v3.6.0/mapbox-gl.js';
  var GL_CSS = 'https://api.mapbox.com/mapbox-gl-js/v3.6.0/mapbox-gl.css';

  var mapWrap = root.querySelector('[data-ol-pro-map]');
  var mapCanvas = root.querySelector('[data-ol-pro-map-canvas]');
  var token = root.dataset.olProToken || '';
  var styleUrl = root.dataset.olProStyle || 'mapbox://styles/mapbox/dark-v11';
  var map = null;
  var youMarker = null;

  var pinned = cards.filter(function (card) {
    return !isNaN(parseFloat(card.dataset.lat)) && !isNaN(parseFloat(card.dataset.lng));
  });

  /* A <script>/<link> that failed once is dead — the browser will not retry
     it, and a later attempt that merely waits on its `load` waits forever.
     Any tag that errored, or that never resolved either way (a stalled
     connection, a captive portal), is thrown away so the next attempt gets
     a fresh element. */
  var ASSET_TIMEOUT = 8000;
  var loadOnce = function (tag, attrs) {
    return new Promise(function (resolve, reject) {
      var key = attrs.src || attrs.href;
      var found = document.querySelector(tag + '[data-ol-asset="' + key + '"]');
      if (found && found.dataset.olLoaded) return resolve();
      if (found) found.parentNode.removeChild(found);

      var el = document.createElement(tag);
      Object.keys(attrs).forEach(function (k) {
        el.setAttribute(k, attrs[k]);
      });
      el.setAttribute('data-ol-asset', key);

      var settled = false;
      var finish = function (ok) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (ok) {
          el.dataset.olLoaded = '1';
          resolve();
          return;
        }
        if (el.parentNode) el.parentNode.removeChild(el);
        reject();
      };
      var timer = setTimeout(function () {
        finish(false);
      }, ASSET_TIMEOUT);

      el.addEventListener('load', function () {
        finish(true);
      });
      el.addEventListener('error', function () {
        finish(false);
      });
      document.head.appendChild(el);
    });
  };

  var selectCard = function (card, pan) {
    cards.forEach(function (c) {
      c.classList.toggle('is-active', c === card);
    });
    card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    if (pan && map) {
      map.easeTo({
        center: [parseFloat(card.dataset.lng), parseFloat(card.dataset.lat)],
        zoom: Math.max(map.getZoom(), 12),
        duration: 600,
      });
    }
  };

  var makePin = function (card) {
    var el = document.createElement('button');
    el.type = 'button';
    el.className = 'ol-pro__pin';
    el.setAttribute('aria-label', (card.querySelector('.ol-pro__name') || {}).textContent || 'Installer');
    el.innerHTML = '<span class="ol-pro__pin-core"></span><span class="ol-pro__pin-ring"></span>';
    el.addEventListener('click', function (e) {
      e.stopPropagation();
      selectCard(card, true);
    });
    return el;
  };

  var buildMap = function () {
    if (map || !mapCanvas || !window.mapboxgl) return;
    var mapboxgl = window.mapboxgl;
    mapboxgl.accessToken = token;

    map = new mapboxgl.Map({
      container: mapCanvas,
      style: styleUrl,
      center: pinned.length
        ? [parseFloat(pinned[0].dataset.lng), parseFloat(pinned[0].dataset.lat)]
        : [-98.35, 39.5],
      zoom: pinned.length ? 10 : 3,
      attributionControl: false,
      /* Locked on arrival. Every other sheet in the pager is left by a
         vertical swipe, and a map that grabs drags would break that — so
         panning and zooming stay off until the visitor explicitly unlocks
         the map with the button below. */
      scrollZoom: false,
      dragPan: false,
      touchZoomRotate: false,
      doubleClickZoom: false,
      pitchWithRotate: false,
      dragRotate: false,
    });

    // Mapbox requires attribution; compact keeps it to a single "i".
    /* No on-map chrome: the zoom buttons and the compact attribution "i"
       are both removed by request, matching the Optimal app, which also
       runs with attributionEnabled={false}. See the note in the handover —
       Mapbox's terms do require attribution to appear somewhere. */

    var unlockBtn = root.querySelector('[data-ol-pro-unlock]');
    if (unlockBtn) {
      var setLive = function (on) {
        root.classList.toggle('ol-pro--map-live', on);
        unlockBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
        var fn = on ? 'enable' : 'disable';
        map.dragPan[fn]();
        map.touchZoomRotate[fn]();
        map.doubleClickZoom[fn]();
        if (on) map.scrollZoom.enable();
        else map.scrollZoom.disable();
      };
      unlockBtn.addEventListener('click', function () {
        if (!root.classList.contains('ol-pro--map-live')) {
          setLive(true);
          return;
        }
        /* Second press is SEARCH HERE: lock the map again, re-rank every
           dealer against whatever the visitor has centred, relabel with that
           postcode, and bring the rail back. The map is deliberately left
           exactly where they put it — re-framing here would undo the pan
           they just made. */
        setLive(false);
        var c = map.getCenter();
        sortByDistance(c.lat, c.lng, { marker: false, frame: false });
        setCollapsed(false);
      });
    }

    map.on('load', function () {
      root.classList.add('ol-pro--map-ready');
      map.resize();
    });

    /* The sheet is laid out by flex and the panel's height is a percentage,
       so the container's final size is not known when Mapbox first measures
       it — it came out 300px tall inside a 772px box. Watch the element and
       tell the map whenever that changes. */
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        map.resize();
      }).observe(mapCanvas);
    } else {
      window.addEventListener('resize', function () {
        map.resize();
      });
    }

    map.on('error', function () {
      // Bad token, blocked domain, or offline — drop back to the list-only
      // layout rather than leaving a dead black panel.
      root.classList.remove('ol-pro--map');
      root.classList.add('ol-pro--map-failed');
    });

    var bounds = null;
    pinned.forEach(function (card) {
      var lng = parseFloat(card.dataset.lng);
      var lat = parseFloat(card.dataset.lat);
      new mapboxgl.Marker({ element: makePin(card), anchor: 'center' })
        .setLngLat([lng, lat])
        .addTo(map);
      bounds = bounds ? bounds.extend([lng, lat]) : new mapboxgl.LngLatBounds([lng, lat], [lng, lat]);
    });

    if (pinned.length > 1) {
      map.fitBounds(bounds, { padding: { top: 90, bottom: 320, left: 40, right: 40 }, maxZoom: 12 });
    } else if (pinned.length === 1) {
      // Offset upward so the single pin is not hidden behind the panel.
      map.setCenter([parseFloat(pinned[0].dataset.lng), parseFloat(pinned[0].dataset.lat)]);
      map.panBy([0, 110], { duration: 0 });
    }
  };

  /* One attempt used to be the whole story: `mapRequested` was latched
     before anything could go wrong, so a single stumble — a stalled script
     on a cold connection, a momentary DNS failure on refresh — left the
     sheet with no map for the life of the page and nothing that would ever
     try again. That is the "sometimes it just doesn't load" case. It now
     backs off and retries, and gives up on the map layout only once the
     attempts are spent. */
  var MAP_ATTEMPTS = 3;
  var mapAttempt = 0;
  var mapRequested = false;

  var ensureMap = function (force) {
    if (!mapWrap || map) return;
    if (force) {
      // Arriving at the sheet is a fresh chance, whatever happened earlier.
      mapAttempt = 0;
      mapRequested = false;
      root.classList.remove('ol-pro--map-failed');
      root.classList.add('ol-pro--map');
    }
    if (mapRequested) return;
    if (!token) {
      root.classList.remove('ol-pro--map');
      root.classList.add('ol-pro--map-failed');
      return;
    }
    mapRequested = true;
    mapAttempt += 1;

    loadOnce('link', { rel: 'stylesheet', href: GL_CSS })
      .then(function () {
        return loadOnce('script', { src: GL_JS });
      })
      .then(function () {
        buildMap();
        // buildMap bails silently if the global still is not there.
        if (!map) throw new Error('mapboxgl unavailable');
      })
      .catch(function () {
        mapRequested = false;
        if (mapAttempt >= MAP_ATTEMPTS) {
          // Out of tries: fall back to the list-only layout for good.
          root.classList.remove('ol-pro--map');
          root.classList.add('ol-pro--map-failed');
          return;
        }
        setTimeout(ensureMap, 700 * mapAttempt);
      });
  };

  /* Built up front, not on arrival. This is the last sheet in the pager, so
     waiting for it to come into view meant every visitor watched the map
     assemble itself after they had already swiped there. Deferred to idle so
     it does not compete with the hero on first paint, with a timeout so it
     happens regardless on a busy page. */
  if (mapWrap) {
    if ('requestIdleCallback' in window) {
      // Wrapped: requestIdleCallback hands the callback an IdleDeadline,
      // which would arrive as ensureMap's `force` argument and reset the
      // attempt counter on the very first run.
      requestIdleCallback(
        function () {
          ensureMap();
        },
        { timeout: 2500 }
      );
    } else {
      setTimeout(ensureMap, 1200);
    }
  }

  /* ---- Rail: collapse, and hide when there is nothing to show ---- */
  var panel = root.querySelector('[data-ol-pro-panel]');
  var collapseBtn = root.querySelector('[data-ol-pro-collapse]');
  var reopenBtn = root.querySelector('[data-ol-pro-reopen]');
  var reopenCount = root.querySelector('[data-ol-pro-reopen-count]');

  var setCollapsed = function (on) {
    root.classList.toggle('ol-pro--collapsed', on);
    if (collapseBtn) collapseBtn.setAttribute('aria-expanded', on ? 'false' : 'true');
    // The map's usable width changes with the rail, so re-centre on it.
    if (map) setTimeout(function () { map.resize(); }, 320);
  };

  if (collapseBtn) {
    collapseBtn.addEventListener('click', function () {
      setCollapsed(true);
    });
  }
  if (reopenBtn) {
    reopenBtn.addEventListener('click', function () {
      setCollapsed(false);
    });
  }

  /* The rail is only meaningful when it has results in it — with none, it is
     a blank box sitting on the map. Hidden in that case so the sheet is just
     the map and the search field. */
  var syncRail = function (shown) {
    root.classList.toggle('ol-pro--empty', shown === 0);
    if (reopenCount) reopenCount.textContent = shown ? String(shown) : '';
  };

  // Tapping a card anywhere but its buttons focuses it on the map.
  cards.forEach(function (card) {
    card.addEventListener('click', function (e) {
      if (e.target.closest('a')) return;
      selectCard(card, !isNaN(parseFloat(card.dataset.lat)));
    });
  });

  var located = false;

  /* `silent` is the automatic attempt made when the sheet is first reached.
     It says nothing on failure: a visitor who declines the permission prompt
     should not be met with an error, the search field still does the job.
     The button press is the loud version, which does explain itself. */
  var requestLocation = function (silent) {
    /* The `located` guard only applies to the automatic attempts. A deliberate
       press must always re-request: after SEARCH HERE has re-ranked the list
       around some other ZIP, this button is the only way back to the
       visitor's own position, and it used to no-op once a fix had been
       obtained. */
    if (located && silent) return;
    if (!navigator.geolocation) {
      if (!silent) setStatus('LOCATION UNAVAILABLE');
      return;
    }
    if (locate) locate.classList.add('is-busy');
    if (!silent) setStatus('LOCATING…');
    navigator.geolocation.getCurrentPosition(
      function (pos) {
        located = true;
        if (locate) {
          locate.classList.remove('is-busy');
          locate.classList.add('is-on');
        }
        sortByDistance(pos.coords.latitude, pos.coords.longitude);
      },
      function () {
        if (locate) locate.classList.remove('is-busy');
        // No search field to fall back on any more — point at the setting.
        if (!silent) setStatus('LOCATION OFF — ENABLE IN SETTINGS');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  };

  if (locate) {
    locate.addEventListener('click', function () {
      requestLocation(false);
    });
  }

  /* Ask as soon as the sheet is actually reached, not on page load — the
     prompt then arrives in context, next to a list of installers, instead of
     ambushing someone on the hero. */
  /* Park the rail below whatever height the header actually is, rather than
     a guessed constant — the title wraps differently by viewport and the
     search field grows with its font size. */
  var topBar = root.querySelector('.ol-pro__top');
  var RAIL_GAP = 12; // matches the rail's bottom inset in the stylesheet
  /* Where the rail sits in the space between the title and the foot of the
     map: 0 pins it under the title, 0.5 centres it, 1 drops it to the
     bottom. Biased past centre because the map is most worth seeing
     directly under the title, where the search you just made is centred. */
  var RAIL_BIAS = 0.78;

  var placeRail = function () {
    if (!topBar) return;
    /* With a search bar the rail takes its slot; without one it sits below
       the title instead. This is the HIGHEST the rail may sit — the top of
       the space it has to work in, not where it ends up. */
    var search = root.querySelector('.ol-pro__search');
    var rootRect = root.getBoundingClientRect();
    var ceiling = search
      ? search.getBoundingClientRect().top - rootRect.top
      : topBar.getBoundingClientRect().bottom - rootRect.top + 14;

    /* Two passes. Place it at the ceiling first so the rail is measured
       and trimmed against the largest slot it could have, then centre what
       is left in the gap between the title and the foot of the map. Moving
       it down shortens its own max-height, which can force another tile
       off — so re-trim and re-centre once against the height it actually
       ends up with, and the second pass settles because trimming only ever
       makes the rail shorter. */
    var settle = function (top) {
      root.style.setProperty('--ol-pro-rail-top', Math.round(top) + 'px');
      capList();
      if (!panel) return top;
      var free = rootRect.height - ceiling - RAIL_GAP - panel.getBoundingClientRect().height;
      return free > 0 ? ceiling + free * RAIL_BIAS : ceiling;
    };

    settle(settle(ceiling));
  };
  placeRail();
  if ('ResizeObserver' in window && topBar) {
    new ResizeObserver(placeRail).observe(topBar);
  }
  window.addEventListener('resize', placeRail);

  syncRail(cards.length);
  capList();

  /* Asked on load rather than on arrival, so the list is already sorted and
     the map already framed by the time anyone swipes down to this sheet.
     The silent flag means a refusal is invisible — no error is shown and the
     search field still does the job. The sheet observer below stays as a
     second chance for anyone who declines now and reaches the locator
     later. */
  if ('requestIdleCallback' in window) {
    requestIdleCallback(
      function () {
        requestLocation(true);
      },
      { timeout: 3000 }
    );
  } else {
    setTimeout(function () {
      requestLocation(true);
    }, 1500);
  }

  if ('IntersectionObserver' in window) {
    var lo = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.intersectionRatio > 0.4) {
            requestLocation(true);
            /* Last line of defence for the map. If the up-front attempts all
               failed while the visitor was elsewhere in the stack, arriving
               at the sheet starts them over — by now the connection has had
               several more seconds, and this is the one moment where a
               missing map is actually being looked at. Cheap when the map is
               already up: ensureMap() returns immediately. */
            ensureMap(true);
            lo.disconnect();
          }
        });
      },
      { threshold: [0, 0.4] }
    );
    lo.observe(root);
  }
})();

/* ---------- Cart sheet: swipe down to dismiss ----------
   The close button is gone, so the sheet needs the gesture the shape of it
   promises. A strip across the top of the sheet takes the drag; the list
   below it keeps its own scrolling, which is why the gesture is scoped to
   the strip rather than to the whole sheet.

   The sheet follows the finger exactly — transition off while dragging, an
   inline transform written straight to the compositor — and the dim behind
   it fades with the travel, so letting go halfway shows you exactly how far
   from dismissing it you are. Past a quarter of the way down, or with any
   real speed behind it, it goes; short of that it springs back. */
(function () {
  var DISMISS_FRACTION = 0.25; // of the sheet's height
  var DISMISS_VELOCITY = 0.5; // px/ms
  var VELOCITY_WINDOW = 90; // ms

  var start = function () {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var inner = drawer.querySelector('.drawer__inner');
    if (!inner || inner.querySelector('.ol-cart-grip')) return;
    var overlay = drawer.querySelector('.cart-drawer__overlay');

    var grip = document.createElement('div');
    grip.className = 'ol-cart-grip';
    grip.setAttribute('aria-hidden', 'true');
    grip.appendChild(document.createElement('span')).className = 'ol-cart-grip__bar';
    inner.insertBefore(grip, inner.firstChild);

    var startY = 0;
    var height = 0;
    var offset = 0;
    var dragging = false;
    var sampleY = [];
    var sampleT = [];
    var raf = null;

    /* One write per FRAME, not one per event. Touch events outrun the
       display — on a 120Hz panel a slow drag can deliver several moves
       between paints — and every extra write is a style recalculation that
       buys nothing, because only the last value before the frame is ever
       seen. Same rAF batching the sheet pager uses. */
    var paint = function () {
      raf = null;
      inner.style.transform = 'translate3d(0,' + offset + 'px,0)';
      if (overlay) overlay.style.opacity = String(Math.max(0, 1 - offset / height));
    };

    var schedule = function () {
      if (raf === null) raf = requestAnimationFrame(paint);
    };

    /* Hands the sheet back to the stylesheet. Clearing the inline transform
       in the same frame the class changes is what lets the CSS transition
       carry it the rest of the way — to translateY(100%) if `active` has
       just been dropped, back to 0 if it has not. */
    var handBack = function () {
      if (raf !== null) {
        cancelAnimationFrame(raf);
        raf = null;
      }
      drawer.classList.remove('is-dragging');
      inner.classList.remove('is-dragging');
      grip.classList.remove('is-dragging');
      inner.style.transform = '';
      if (overlay) overlay.style.opacity = '';
    };

    grip.addEventListener(
      'touchstart',
      function (e) {
        if (e.touches.length !== 1) return;
        dragging = true;
        offset = 0;
        startY = e.touches[0].clientY;
        height = inner.getBoundingClientRect().height || 1;
        sampleY = [startY];
        sampleT = [e.timeStamp];
        /* On the drawer as well as the sheet: the dim is a sibling, and it
           needs its own transition killed and its own layer for the drag. */
        drawer.classList.add('is-dragging');
        inner.classList.add('is-dragging');
        grip.classList.add('is-dragging');
      },
      { passive: true }
    );

    grip.addEventListener(
      'touchmove',
      function (e) {
        if (!dragging || e.touches.length !== 1) return;
        var y = e.touches[0].clientY;
        /* Down only. Dragging up would lift the sheet off the bottom of the
           screen and show the page through the gap under it. */
        offset = Math.max(0, y - startY);
        e.preventDefault();
        schedule();

        sampleY.push(y);
        sampleT.push(e.timeStamp);
        while (sampleT.length > 2 && e.timeStamp - sampleT[0] > VELOCITY_WINDOW) {
          sampleY.shift();
          sampleT.shift();
        }
      },
      { passive: false }
    );

    var end = function (e) {
      if (!dragging) return;
      dragging = false;
      var velocity = 0;
      var n = sampleT.length;
      if (n > 1 && e.timeStamp - sampleT[n - 1] <= VELOCITY_WINDOW) {
        velocity = (sampleY[n - 1] - sampleY[0]) / Math.max(1, sampleT[n - 1] - sampleT[0]);
      }
      var dismiss = offset > height * DISMISS_FRACTION || velocity > DISMISS_VELOCITY;
      if (dismiss) drawer.close();
      handBack();
    };

    grip.addEventListener('touchend', end, { passive: true });
    grip.addEventListener('touchcancel', end, { passive: true });
  };

  /* ---------- The empty sheet's one button ----------
     Adds the kit in place and lets Dawn re-render the drawer around it, so
     the sheet never closes and reopens — the customer watches an empty cart
     become a full one. `sections` asks the cart endpoint for the same two
     fragments Dawn's own add-to-cart flow uses, which is what renderContents
     expects to be handed.

     Delegated from the document: the button lives inside the markup Dawn
     replaces wholesale on every cart change, so a listener bound to the
     element itself would survive exactly one render. */
  var wireAdd = function () {
    document.addEventListener('click', function (event) {
      var target = event.target;
      var button = target && target.closest ? target.closest('[data-ol-cart-add]') : null;
      if (!button || button.hasAttribute('aria-busy')) return;
      var variant = parseInt(button.getAttribute('data-ol-cart-add'), 10);
      var drawer = document.querySelector('cart-drawer');
      if (!variant || !drawer || typeof drawer.renderContents !== 'function') return;
      event.preventDefault();
      // Guards the double-tap: a second add before the first returns would
      // silently put two kits in the cart.
      button.setAttribute('aria-busy', 'true');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variant, quantity: 1 }],
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (state) {
          if (state && state.sections) drawer.renderContents(state);
        })
        .catch(function () {
          /* The sheet stays open and the button comes back, rather than
             throwing the customer onto /cart. A failed add is usually
             transient — a dropped connection, a throttled storefront, a
             response that is not JSON — and another tap is a better answer
             than a page they did not ask for. */
        })
        .then(function () {
          button.removeAttribute('aria-busy');
        });
    });
  };

  /* ---------- "Will you need a pro?" ----------
     A question, not an offer to be waved away: it stays put, and the answer
     sticks to whichever button gave it. The choice is kept in
     sessionStorage so it survives the re-render Dawn does on every cart
     change and the trip out to the Find a Pro sheet and back — without it,
     answering YES would land the customer on the pro sheet and then show
     them an unanswered question the moment they reopened the cart.
     Session-scoped rather than local: it is an answer about this order.

     YES is also a [data-ol-sheet] link, so the pager's own listener takes
     it to the Find a Pro sheet; all this adds is closing the cart first so
     that sheet is not arriving behind an open drawer.

     Wrapped in try/catch because Safari throws on storage in private mode
     rather than returning null. */
  var PRO_KEY = 'ol-cart-pro-choice';

  var proChoice = function () {
    try {
      return window.sessionStorage.getItem(PRO_KEY);
    } catch (error) {
      return null;
    }
  };

  /* Paints the answer onto whatever markup is currently in the sheet. Runs
     on every render, not just on click, because Dawn reprints the drawer
     wholesale and the fresh copy comes back unanswered. */
  var syncPro = function () {
    var drawer = document.querySelector('cart-drawer');
    var block = document.querySelector('[data-ol-cart-pro]');
    var choice = proChoice();

    if (block) {
      var buttons = block.querySelectorAll('[data-ol-cart-pro-choice]');
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].setAttribute(
          'aria-pressed',
          choice && buttons[i].getAttribute('data-ol-cart-pro-choice') === choice ? 'true' : 'false'
        );
      }
      if (choice) block.setAttribute('data-answered', choice);
      else block.removeAttribute('data-answered');
    }

    /* The gate has three states, not two:

         locked — the question is on screen and unanswered. Checkout is
                  dimmed and blocked until they answer.
         pro    — they answered YES. They are not the one placing this
                  order, so checkout and the wallets come off the sheet
                  and a line stands in their place.
         open   — they answered NO, or there is no question to answer.

       `block` has to exist for any of it: an empty cart has no question in
       it, and locking checkout over a question nobody was asked would be a
       dead end. */
    if (!drawer) return;
    var gate = !block ? 'open' : choice === 'yes' ? 'pro' : choice ? 'open' : 'locked';
    drawer.setAttribute('data-ol-gate', gate);
    var checkout = drawer.querySelector('.cart__checkout-button');
    if (checkout) {
      // Only meaningful in `locked`; in `pro` the button is not on screen.
      if (gate === 'locked') checkout.setAttribute('aria-disabled', 'true');
      else checkout.removeAttribute('aria-disabled');
    }
  };

  /* Sends the customer back to the question and knocks it once. The class is
     pulled off on the way in so an answer given, undone and asked for again
     re-triggers the animation rather than sitting there already-animated. */
  var flagPro = function () {
    var block = document.querySelector('[data-ol-cart-pro]');
    if (!block) return;
    block.classList.remove('is-missing');
    // Reading offsetWidth forces the style change to land before the class
    // goes back on; without it the browser coalesces both and nothing moves.
    void block.offsetWidth;
    block.classList.add('is-missing');
    if (block.scrollIntoView) block.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    window.setTimeout(function () {
      block.classList.remove('is-missing');
    }, 700);
  };

  /* Anything but `open` means nothing may start a checkout — in `pro` the
     controls are already off the sheet, and this is the backstop for a
     keyboard submit or a stray programmatic click. */
  var isLocked = function () {
    var drawer = document.querySelector('cart-drawer');
    return !!drawer && drawer.getAttribute('data-ol-gate') !== 'open';
  };

  var isProAnswered = function () {
    var drawer = document.querySelector('cart-drawer');
    return !!drawer && drawer.getAttribute('data-ol-gate') === 'pro';
  };

  /* Closes the cart and hands over to the pager. `olGoToSheet` is the pager's
     own jump, so the sheet arrives on the same animation the menu links use;
     if the pager is not running on this page it returns false and a plain
     navigation does the job. */
  var sendToPro = function () {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
    if (typeof window.olGoToSheet === 'function' && window.olGoToSheet('find_a_pro')) return;
    window.location.href = '/?olsheet=find_a_pro';
  };

  /* The answer rides along on the cart itself, so it reaches the order in
     admin instead of living and dying in the browser. Fire and forget: a
     failed attribute write must not stop the sheet from showing the answer,
     and it is not worth a second of the customer's time either way. */
  var recordPro = function (choice) {
    // An empty string is how Shopify deletes a cart attribute, which is what
    // un-selecting has to do — leaving a stale Yes/No on the order would be
    // worse than never having asked.
    var value = choice === 'yes' ? 'Yes' : choice === 'no' ? 'No' : '';
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { 'Needs a pro install': value } }),
    }).catch(function () {});
  };

  var storePro = function (choice) {
    try {
      if (choice) window.sessionStorage.setItem(PRO_KEY, choice);
      else window.sessionStorage.removeItem(PRO_KEY);
    } catch (error) {
      /* Private mode: the answer shows for this open, not the next. */
    }
  };

  var wirePro = function () {
    /* Capture phase, because YES is also a [data-ol-sheet] link and the
       pager's own listener is waiting for it on the bubble. Choosing YES
       should travel; un-choosing it must not, and stopping the event here is
       the only place that is still possible. */
    document.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var button = target.closest('[data-ol-cart-pro-choice]');
        if (!button) return;
        var choice = button.getAttribute('data-ol-cart-pro-choice');
        // Pressing the answer you already gave takes it back.
        var next = proChoice() === choice ? null : choice;

        storePro(next);
        syncPro();
        recordPro(next);

        if (next !== 'yes') {
          // NO never travels, and neither does an undo of either answer.
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        /* Choosing YES: get the cart out of the way and let the event carry
           on to the pager, which takes them to the Find a Pro sheet. */
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.close === 'function') drawer.close();
      },
      true
    );

    /* Capture phase, so this runs before Dawn's own handlers and before the
       wallet scripts get the event — by the bubble phase the checkout is
       already on its way. */
    document.addEventListener(
      'click',
      function (event) {
        if (!isLocked()) return;
        var target = event.target;
        if (!target || !target.closest) return;
        if (!target.closest('.cart__checkout-button') && !target.closest('.ol-cart-express')) return;
        event.preventDefault();
        event.stopPropagation();
        /* Two reasons the payment area is dead, and they want opposite
           answers. Unanswered: the fix is in the sheet, so knock the
           question. Answered YES: the fix is not in the sheet at all, so
           reaching for pay is a request to go find a pro — take them. */
        if (isProAnswered()) sendToPro();
        else flagPro();
      },
      true
    );

    /* Belt and braces: the form can also be submitted by keyboard, and the
       checkout button reaches it from outside via `form=`. */
    document.addEventListener(
      'submit',
      function (event) {
        if (!isLocked()) return;
        var form = event.target;
        if (!form || form.id !== 'CartDrawer-Form') return;
        event.preventDefault();
        if (isProAnswered()) sendToPro();
        else flagPro();
      },
      true
    );
  };

  /* ---------- Discount code ----------
     /discount/<code> sets the discount cookie and redirects; pointing its
     `redirect` at /cart.js means the round trip ends on the cart JSON
     rather than a full page. Validity is read from `discount_codes[].
     applicable`, never from the money: a free-shipping code is applicable
     and still leaves total_price untouched, so a price comparison would
     reject codes that are perfectly good. The cart is re-read on its own
     rather than trusted from the redirect body, so the answer can't be a
     frame behind the cookie.

     Delegated from the document, like the add button: the footer is inside
     the markup Dawn replaces wholesale on every cart change.

     There is no Shopify endpoint to take a code back off, so there is no
     remove control here — a second code replaces the first. */
  var wireCode = function () {
    var busy = false;

    var note = function (message, state) {
      var el = document.querySelector('[data-ol-cart-code-note]');
      if (!el) return;
      el.textContent = message || '';
      if (state) el.setAttribute('data-state', state);
      else el.removeAttribute('data-state');
      el.hidden = !message;
    };

    var apply = function () {
      if (busy) return;
      var input = document.querySelector('[data-ol-cart-code]');
      var button = document.querySelector('[data-ol-cart-code-apply]');
      if (!input) return;
      var code = (input.value || '').trim();
      if (!code) {
        input.focus();
        return;
      }
      busy = true;
      if (button) button.setAttribute('aria-busy', 'true');
      note('Checking\u2026');

      fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', {
        credentials: 'same-origin',
      })
        .then(function () {
          return fetch('/cart.js', { credentials: 'same-origin' });
        })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          var codes = (cart && cart.discount_codes) || [];
          var wanted = code.toUpperCase();
          var applicable = false;
          for (var i = 0; i < codes.length; i++) {
            if (String(codes[i].code).toUpperCase() === wanted) {
              applicable = codes[i].applicable === true;
              break;
            }
          }
          if (!applicable) {
            note('That code is not valid.', 'error');
            return null;
          }
          // Re-render so the discount line and the new total come from
          // Liquid, the same way every other cart change does.
          var drawer = document.querySelector('cart-drawer');
          if (!drawer || typeof drawer.renderContents !== 'function') {
            note('Code applied.');
            return null;
          }
          return fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble')
            .then(function (response) {
              return response.json();
            })
            .then(function (sections) {
              if (sections) drawer.renderContents({ sections: sections });
              // After the render, so it lands on the fresh markup rather
              // than the copy that was just thrown away.
              note('Code applied.');
            });
        })
        .catch(function () {
          note('Could not apply that code. Try again.', 'error');
        })
        .then(function () {
          busy = false;
          var live = document.querySelector('[data-ol-cart-code-apply]');
          if (live) live.removeAttribute('aria-busy');
        });
    };

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      if (!target.closest('[data-ol-cart-code-apply]')) return;
      event.preventDefault();
      apply();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      var target = event.target;
      if (!target || !target.closest || !target.closest('[data-ol-cart-code]')) return;
      event.preventDefault();
      apply();
    });
  };

  /* ---------- Keeping `is-empty` honest ----------
     Dawn's renderContents() clears `is-empty` from .drawer__inner and STOPS
     THERE — the <cart-drawer> element keeps whatever Liquid printed on it at
     page load, for the life of the page. The short empty-cart sheet is sized
     off that element, so adding a kit left the sheet at its empty height and
     the product was cut off inside it. Read from the line items instead,
     which is the only thing that actually knows. */
  var syncEmpty = function () {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var filled = !!drawer.querySelector('.cart-item');
    drawer.classList.toggle('is-empty', !filled);
  };

  /* Dawn rebuilds #CartDrawer's innerHTML on every cart change — adding an
     item, changing a quantity — which throws the grip away along with it.
     Watching the container puts it back each time; start() is a no-op when
     a grip is already there, so this costs nothing on unrelated mutations. */
  var watch = function () {
    start();
    syncEmpty();
    syncPro();
    wireAdd();
    wireCode();
    wirePro();
    var host = document.getElementById('CartDrawer');
    if (!host || !window.MutationObserver) return;
    new MutationObserver(function () {
      start();
      syncEmpty();
      syncPro();
    }).observe(host, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();
